<!-- 
  ========================================================================
  ⚠️ CRITICAL DEVELOPMENT WARNING (PLEASE READ BEFORE EDITING) ⚠️
  ========================================================================
  This project maintains two synchronized home page files:
  1. frontend/src/pages/Home.vue (The active home page)
  2. frontend/src/pages/Home_restored.vue (The master template backup)

  Why? 
  Encoding corruption (mojibake) sometimes occurs on this codebase. 
  Restoration scripts (like run_restoration.cjs) clean up mojibake by reading 
  from Home_restored.vue, applying clean Thai character translations, 
  and overwriting Home.vue.

  Action required:
  If you edit the home page layout or logic in Home.vue, you MUST manually 
  copy your changes to Home_restored.vue (or run 'npm run deploy' which syncs them).
  Failure to do so will cause the next repair script execution to wipe out 
  your updates and revert the UI to the older version!
  ========================================================================
-->
<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, reactive, watch } from 'vue'
import { useTrackingStore } from '../store/tracking'
import { useCartStore } from '../stores/cartStore'
import { useSettingsStore } from '../stores/settingsStore'
import { useWishlistStore } from '../stores/wishlistStore'
import { useCompareStore } from '../stores/compareStore'
import { useAuthStore } from '../stores/authStore'
import { useToast } from '../composables/useToast'
import AOS from 'aos'
import FeatureIcon from '../components/ui/FeatureIcon.vue'
import ProductCard from '../components/ProductCard.vue'
import { getOptimizedImageUrl } from '../utils/image'

const trackingStore = useTrackingStore()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()
const wishlistStore = useWishlistStore()
const compareStore = useCompareStore()
const authStore = useAuthStore()
const { showToast } = useToast()

const contactPhones = computed(() => settingsStore.contactPhones)
const contactLines = computed(() => settingsStore.contactLines)

const primaryPhone = computed(() => {
  if (contactPhones.value && contactPhones.value.length > 0) {
    return contactPhones.value[0].value
  }
  return settingsStore.storePhone || '02-123-4567'
})

const primaryPhoneLink = computed(() => {
  const phone = primaryPhone.value || '02-123-4567'
  return 'tel:' + phone.replace(/[^0-9+]/g, '')
})

const primaryLine = computed(() => {
  if (contactLines.value && contactLines.value.length > 0) {
    const line = contactLines.value[0]
    return {
      url: line.url || 'https://line.me/ti/p/~' + line.value.replace(/^@/, ''),
      value: line.value
    }
  }
  return {
    url: 'https://line.me',
    value: ''
  }
})

// Contact Dropdown State
const showContactDropdown = ref(false)
const dropdownRef = ref(null)

const toggleContactDropdown = (e) => {
  e.preventDefault()
  e.stopPropagation()
  showContactDropdown.value = !showContactDropdown.value
}

const closeContactDropdown = (e) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    showContactDropdown.value = false
  }
}

const addToCart = async (product) => {
  try {
    await cartStore.addToCart(product, 1);
    showToast(`เพิ่ม ${product.title} ลงในตะกร้าแล้ว`, 'success');
  } catch (err) {
    showToast('ไม่สามารถเพิ่มลงตะกร้าได้', 'error');
  }
}

const toggleWishlistItem = async (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  if (!authStore.isAuthenticated) {
    showToast('กรุณาเข้าสู่ระบบก่อนเพิ่มรายการโปรด', 'warning');
    return;
  }
  const result = await wishlistStore.toggleWishlist(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการโปรดแล้ว' : 'นำออกจากรายการโปรดแล้ว', result.isAdded ? 'success' : 'info');
  }
}

const toggleCompareItem = (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  const result = compareStore.toggleCompare(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการเปรียบเทียบแล้ว' : 'นำออกจากรายการเปรียบเทียบแล้ว', result.isAdded ? 'success' : 'info');
  } else if (result.error === 'max_reached') {
    showToast('เปรียบเทียบได้สูงสุด 4 ชิ้น', 'warning');
  }
}

const formatArticleDate = (dateString) => {
  if (!dateString) return 'ล่าสุด';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'ล่าสุด';
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch(e) {
    return 'ล่าสุด';
  }
}



// Mouse parallax tracking
const mouse = reactive({ x: 0, y: 0 })
const heroRef = ref(null)

const onMouseMove = (e) => {
  if (!heroRef.value) return
  const rect = heroRef.value.getBoundingClientRect()
  // Normalize to -1 to 1 range
  mouse.x = ((e.clientX - rect.left) / rect.width - 0.5) * 2
  mouse.y = ((e.clientY - rect.top) / rect.height - 0.5) * 2
}

// Computed parallax transforms for different layers  
const parallaxSlow = computed(() => 'translate(0px, 0px)')
const parallaxMedium = computed(() => 'translate(0px, 0px)')
const parallaxFast = computed(() => 'translate(0px, 0px)')
const parallaxReverse = computed(() => 'translate(0px, 0px)')
const parallaxImage = computed(() => 'translate(0px, 0px)')

const trackCTA = (action) => {
  trackingStore.trackEvent({ type: 'click_cta', action })
}



// Home State from Settings API
const settingsLoading = ref(true)
const slides = ref([])
const currentSlide = ref(0)
const slideProgress = ref(0)
let progressInterval = null
const stats = ref([])
const consultantAvatar = ref('')
const consultantTitle = ref('')
const consultantDesc = ref('')
const consultantCtaText = ref('')
const consultantCtaLink = ref('')
const consultantBullets = ref([])
const homeShowHighlightCategories = ref(true)
const featuresHeading = ref('')
const featuresTitle = ref('')
const featuresDesc = ref('')
const featuresItems = ref([])
const featuresImage = ref('')
const featuresBadgeTitle = ref('')
const featuresBadgeDesc = ref('')
const defaultTestimonials = [
  {
    name: 'คุณปิติ',
    role: 'ผู้ประกอบการ',
    stars: 5,
    text: 'ก่อนหน้านี้ผมมักมีปัญหากับการจัดเก็บของที่ไม่ค่อยได้ใช้งานบ่อยๆ จนพื้นที่ในบ้านเริ่มคับแคบ เมื่อตัดสินใจเลือกใช้โรงเก็บของ Morespace สิ่งที่ประทับใจที่สุดคือดีไซน์ที่เรียบหรูสไตล์มินิมอล ซึ่งเข้ากันได้ดีกับบ้านโมเดิร์นของผมอย่างไม่น่าเชื่อ ไม่เหมือนโรงเก็บของทั่วไปที่มักจะดูเป็นส่วนเกิน วัสดุเกรดพรีเมียมจากอเมริกาให้ความรู้สึกแข็งแรงทนทาน และมั่นใจได้ในระยะยาว การติดตั้งก็รวดเร็วและทีมงานมืออาชีพมากครับ ตอนนี้บ้านของผมเป็นระเบียบและยังคงความสวยงาม ผมแนะนำ Morespace สำหรับใครที่มองหาโซลูชันการจัดเก็บที่เหนือระดับจริงๆ ครับ'
  },
  {
    name: 'คุณกานต์ชนก',
    role: 'สถาปนิกจัดสวน',
    stars: 5,
    text: 'ในฐานะสถาปนิกจัดสวน การเลือกโรงเรือนสำหรับปลูกต้นไม้เป็นเรื่องที่พิถีพิถันมากค่ะ เพราะมันคือส่วนหนึ่งของงานศิลปะ Morespace ตอบโจทย์ได้อย่างสมบูรณ์แบบทั้งในด้านสุนทรียภาพและการใช้งานจริง ดีไซน์มินิมอลที่ทันสมัยทำให้โรงเรือนดูเป็นเหมือนงานประติมากรรมในสวน ไม่ใช่แค่โครงสร้างสำหรับปลูกต้นไม้ แต่ยังเป็นพื้นที่พักผ่อนที่สงบและสวยงาม คุณภาพของวัสดุที่เลือกใช้ก็ยอดเยี่ยม ให้ความรู้สึกพรีเมียมและมั่นคง ทำให้ต้นไม้ที่ดูแลเป็นอย่างดีได้รับการปกป้องอย่างสมบูรณ์แบบตลอดปีค่ะ ขอชื่นชมในความใส่ใจในทุกรายละเอียดจริงๆ ค่ะ'
  },
  {
    name: 'คุณอริยา',
    role: 'นักออกแบบกราฟิก',
    stars: 5,
    text: 'การทำงานจากที่บ้านมักมีข้อจำกัดเรื่องสมาธิ จนกระทั่งได้ Morespace มาเป็นสตูดิโอส่วนตัว สิ่งนี้เปลี่ยนวิธีการทำงานของฉันไปโดยสิ้นเชิงค่ะ บ้านน็อคดาวน์สไตล์อเมริกันหลังนี้ไม่เพียงแต่ติดตั้งได้รวดเร็วอย่างเหลือเชื่อ แต่ยังให้ความรู้สึกกว้างขวาง ปลอดโปร่ง และหรูหราเกินกว่าที่คิดไว้มาก วัสดุที่ใช้เก็บเสียงได้ดีเยี่ยม ทำให้ฉันมีสมาธิกับการออกแบบกราฟิกได้อย่างเต็มที่ และยังเป็นพื้นที่รับรองลูกค้าขนาดย่อมๆ ได้อย่างไม่อายใคร ทุกคนที่มาเยือนต่างชื่นชมในความทันสมัยและฟังก์ชันการใช้งานที่ตอบโจทย์จริงๆ Morespace คือการลงทุนที่คุ้มค่าและยกระดับคุณภาพชีวิตการทำงานของฉันขึ้นอีกเลยค่ะ'
  }
]

const testimonials = ref(defaultTestimonials)
const partners = ref([])
const homeShowAffiliates = ref(true)
const homeShowStats = ref(true)
const homeShowFeatures = ref(true)


const partnerActiveIndex = ref(0)
const partnerTransitionEnabled = ref(true)
const isPartnerSliding = ref(false)
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200)

const updateWidth = () => {
  windowWidth.value = window.innerWidth
}

const visibleCount = computed(() => {
  if (windowWidth.value >= 1024) return 3
  if (windowWidth.value >= 768) return 2
  return 1
})

const mappedPartners = computed(() => {
  if (!partners.value || partners.value.length === 0) return []
  return partners.value.map((partner) => {
    return {
      ...partner,
      displayName: partner.name || '',
      displayDesc: partner.desc || ''
    }
  })
})

const extendedPartners = computed(() => {
  const list = mappedPartners.value
  if (list.length === 0) return []
  return [...list, ...list, ...list]
})

const partnerTranslateX = computed(() => {
  if (extendedPartners.value.length === 0) return 0
  return -partnerActiveIndex.value * (100 / visibleCount.value)
})

let partnerInterval = null

const startPartnerAutoplay = () => {}

const stopPartnerAutoplay = () => {
  if (partnerInterval) {
    clearInterval(partnerInterval)
    partnerInterval = null
  }
}

const nextPartnerSlide = () => {
  const len = mappedPartners.value.length
  if (len === 0 || isPartnerSliding.value) return

  isPartnerSliding.value = true
  partnerTransitionEnabled.value = true
  partnerActiveIndex.value++

  setTimeout(() => {
    const lenVal = mappedPartners.value.length
    if (partnerActiveIndex.value >= lenVal * 2) {
      partnerTransitionEnabled.value = false
      partnerActiveIndex.value = partnerActiveIndex.value - lenVal
    }
    isPartnerSliding.value = false
  }, 500)
}

const prevPartnerSlide = () => {
  const len = mappedPartners.value.length
  if (len === 0 || isPartnerSliding.value) return

  isPartnerSliding.value = true
  partnerTransitionEnabled.value = true
  partnerActiveIndex.value--

  setTimeout(() => {
    const lenVal = mappedPartners.value.length
    if (partnerActiveIndex.value < lenVal) {
      partnerTransitionEnabled.value = false
      partnerActiveIndex.value = partnerActiveIndex.value + lenVal
    }
    isPartnerSliding.value = false
  }, 500)
}

watch(mappedPartners, (newVal) => {
  if (newVal.length > 0 && (partnerActiveIndex.value === 0 || partnerActiveIndex.value < newVal.length)) {
    partnerActiveIndex.value = newVal.length
  }
}, { immediate: true })


const recentProjects = ref([])
const recentArticles = ref([])
const currentPageIndex = ref(0)
let articleInterval = null

const visibleCards = computed(() => {
  if (windowWidth.value >= 1024) return 3
  if (windowWidth.value >= 768) return 2
  return 1
})

const totalPages = computed(() => {
  if (recentArticles.value.length === 0) return 0
  return Math.ceil(recentArticles.value.length / visibleCards.value)
})

watch(totalPages, (newTotal) => {
  if (currentPageIndex.value >= newTotal) {
    currentPageIndex.value = Math.max(0, newTotal - 1)
  }
})

const nextArticleSlide = () => {
  if (totalPages.value <= 1) return
  currentPageIndex.value = (currentPageIndex.value + 1) % totalPages.value
}

const prevArticleSlide = () => {
  if (totalPages.value <= 1) return
  currentPageIndex.value = (currentPageIndex.value - 1 + totalPages.value) % totalPages.value
}

const goToArticleSlide = (index) => {
  if (index >= 0 && index < totalPages.value) {
    currentPageIndex.value = index
  }
}

const startArticleAutoplay = () => {}

const stopArticleAutoplay = () => {
  if (articleInterval) {
    clearInterval(articleInterval)
    articleInterval = null
  }
}
const bookmarkedArticles = ref([2, 3])
const isBookmarked = (id) => bookmarkedArticles.value.includes(id)
const toggleBookmark = (id) => {
  if (isBookmarked(id)) {
    bookmarkedArticles.value = bookmarkedArticles.value.filter(item => item !== id)
  } else {
    bookmarkedArticles.value.push(id)
  }
}
const categories = ref([])
const categoryShowcaseSettings = ref([])
const showcaseCategoriesData = ref([])
const activeShowcaseTab = ref(0)
const homeHighlightSettings = ref(null)
const highlightCategoriesList = ref([])
let slideInterval = null

// --- Default GreenHouse fallback values ---
const defaultFloatingCards = [
  { icon: 'chart', title: 'สภาวะในโรงเรือน', desc: 'อุณหภูมิ 28.4 °C | ความชื้น 72% rH', badgeText: 'Live', showSparkline: true },
  { icon: 'chip', title: 'IoT Controller', desc: 'Online', badgeText: 'Online', showSparkline: false },
  { icon: 'shield', title: 'ปลอดภัย 100%', desc: 'ไร้สารเคมีตกค้าง มาตรฐานสากล', badgeText: '', showSparkline: false }
]

const defaultHeroFeaturesBarItems = [
  { icon: 'water', title: 'ประหยัดน้ำ 90%', desc: 'ลดการใช้ทรัพยากร คุ้มค่าระยะยาว' },
  { icon: 'leaf', title: 'เพิ่มผลผลิต 3-5 เท่า', desc: 'โตเร็ว แข็งแรง ปลอดศัตรูพืช' },
  { icon: 'shield', title: 'โครงสร้างแข็งแรงทนทาน', desc: 'เหล็กกัลวาไนซ์ กันสนิมมาตรฐาน' },
  { icon: 'lightning', title: 'ติดตั้งเร็ว ใช้งานง่าย', desc: 'ทีมช่างมืออาชีพ ดูแลครบวงจร' }
]

const defaultSlides = [
  {
    image: '/images/home/hero-slide-1.png',
    tag: 'QUALITY GREENHOUSE SOLUTIONS',
    titleLine1: 'เนรมิตพื้นที่สวนด้วย',
    titleLine2: 'โรงเรือนปลูกพืชคุณภาพ',
    titleHighlight: 'พรีเมียม',
    desc: 'ปกป้องพืชผลของคุณอย่างสมบูรณ์แบบ ด้วยโครงสร้างโรงเรือนมาตรฐานสากล แข็งแรง ทนทาน และระบายอากาศได้ดี',
    ctaText: 'ดูโซลูชันโรงเรือน',
    ctaLink: '/products',
    ctaAction: 'view_greenhouses'
  },
  {
    image: '/images/home/hero-slide-2.png',
    tag: 'HYDROPONIC & VERTICAL FARMING',
    titleLine1: 'ระบบปลูกพืชไฮโดรโปนิกส์',
    titleLine2: 'ประหยัดน้ำและพื้นที่',
    titleHighlight: '90%',
    desc: 'เทคโนโลยีการปลูกพืชแนวตั้ง ประหยัดพื้นที่ ปลูกพืชได้ทุกฤดูกาล ปลอดภัย ไร้สารเคมีตกค้าง',
    ctaText: 'เลือกระบบปลูกพืช',
    ctaLink: '/products',
    ctaAction: 'view_hydroponics'
  },
  {
    image: '/images/home/hero-slide-3.png',
    tag: 'EXPERT GREENHOUSE CONSULTANT',
    titleLine1: 'ปรึกษาผู้เชี่ยวชาญ',
    titleLine2: 'ออกแบบและติดตั้งโรงเรือน',
    titleHighlight: 'ฟรี',
    desc: 'ประเมินพื้นที่และคำนวณงบประมาณการติดตั้งโรงเรือนที่เหมาะสมกับพืชและความต้องการของคุณ',
    ctaText: 'ขอใบเสนอราคา',
    ctaLink: '/quotation',
    ctaAction: 'consult'
  }
]

const defaultStats = [
  { value: '1,500+', label: 'ฟาร์ม', subLabel: 'ไว้วางใจเรา\nทั่วประเทศ', icon: 'home' },
  { value: '98%', label: 'ความพึงพอใจ', subLabel: 'จากเกษตรกร\nที่ใช้บริการ', icon: 'heart' },
  { value: '40%', label: 'ช่วยประหยัดต้นทุน', subLabel: 'น้ำและปุ๋ย\nเฉลี่ย', icon: 'chart' },
  { value: '24/7', label: 'ระบบติดตามอัจฉริยะ', subLabel: 'ไม่เว้นวันหยุด\nดูแลตลอดเวลา', icon: 'clock' }
]

const defaultSectionTitles = {
  projectsTitle: 'ผลงานติดตั้งโรงเรือน',
  projectsHeading: 'ผลงานโรงเรือนและฟาร์มอัจฉริยะล่าสุด',
  statsBadge: 'SMART FARMING IMPACT',
  statsHeading: 'ผลลัพธ์ที่เกิดขึ้นจริงจากลูกค้าทั่วประเทศ',
  partnersDesc: 'เราได้รับความไว้วางใจจากองค์กรชั้นนำมากมาย ที่เลือกใช้บริการของเรา ในการยกระดับพื้นที่และเพิ่มประสิทธิภาพการจัดเก็บ',
  partnersMotto: 'เรามุ่งมั่นส่งมอบสินค้าคุณภาพและบริการที่เป็นเลิศ เพื่อสร้างความไว้วางใจและความพึงพอใจสูงสุดให้กับทุกองค์กร',
  affiliatesTitle: 'บริษัทในเครือ',
  affiliatesHeading: 'เครือข่ายธุรกิจของเรา',
  affiliatesDesc: 'บริษัทในเครือที่ร่วมขับเคลื่อนธุรกิจด้วยนวัตกรรม คุณภาพ และความเชี่ยวชาญ เพื่อสร้างคุณค่าและเติบโตไปด้วยกันอย่างมั่นคง'
}

const defaultConsultantBullets = [
  { icon: 'shield', title: 'ผู้เชี่ยวชาญ', desc: 'ประสบการณ์จริง' },
  { icon: 'leaf', title: 'ออกแบบเฉพาะ', desc: 'ฟาร์มของคุณ' },
  { icon: 'lock', title: 'ข้อมูลปลอดภัย', desc: 'เป็นความลับ' }
]

const homeShowFloatingCards = ref(false)
const floatingCards = ref([...defaultFloatingCards])
const homeShowHeroFeaturesBar = ref(true)
const heroFeaturesBarItems = ref([...defaultHeroFeaturesBarItems])

// Initialize slides with default fallback immediately
slides.value = [...defaultSlides]
stats.value = [...defaultStats]

// Load Homepage Settings
const loadHomepageSettings = async () => {
  settingsLoading.value = true
  try {
    const res = await fetch('/api/settings')
    const data = await res.json()
    if (data.success && data.data) {
      const s = data.data
      
      try { 
        const parsedSlides = s.home_slides ? JSON.parse(s.home_slides) : [] 
        slides.value = Array.isArray(parsedSlides) && parsedSlides.length > 0 ? parsedSlides : defaultSlides
      } catch(e) {
        slides.value = defaultSlides
      }
      
      // Patch: replace external/broken image URLs with local fallbacks
      const localSlideImages = ['/images/home/hero-slide-1.png', '/images/home/hero-slide-2.png', '/images/home/hero-slide-3.png']
      if (slides.value.length > 0) {
        slides.value = slides.value.map((slide, i) => {
          if (typeof slide !== 'object' || !slide) return { image: localSlideImages[i] || localSlideImages[0] }
          return {
            ...slide,
            image: (slide.image && typeof slide.image === 'string' && !slide.image.startsWith('http') && slide.image.length > 2) ? slide.image : (localSlideImages[i] || localSlideImages[0])
          }
        })
      }
      try { 
        const parsedStats = s.home_stats ? JSON.parse(s.home_stats) : [] 
        stats.value = Array.isArray(parsedStats) && parsedStats.length > 0 ? parsedStats : defaultStats
      } catch(e) {
        stats.value = defaultStats
      }
      try { 
        const parsedFeatures = s.home_features_items ? JSON.parse(s.home_features_items) : [] 
        featuresItems.value = Array.isArray(parsedFeatures) ? parsedFeatures : []
      } catch(e) {}
      try { 
        const parsedTestis = s.home_testimonials ? JSON.parse(s.home_testimonials) : []
        testimonials.value = Array.isArray(parsedTestis) && parsedTestis.length > 0 ? parsedTestis : defaultTestimonials
      } catch(e) {
        testimonials.value = defaultTestimonials
      }
      try { 
        const parsedPartners = s.home_partners ? JSON.parse(s.home_partners) : []
        partners.value = Array.isArray(parsedPartners) ? parsedPartners : []
      } catch(e) {}
      try { 
        const parsedAffiliates = s.home_affiliates ? JSON.parse(s.home_affiliates) : []
        affiliatedCompanies.value = Array.isArray(parsedAffiliates) ? parsedAffiliates : []
      } catch(e) {}
      try { 
        const parsedShowcase = s.home_category_showcase ? JSON.parse(s.home_category_showcase) : []
        categoryShowcaseSettings.value = Array.isArray(parsedShowcase) ? parsedShowcase : []
      } catch(e) {}
      try { 
        const parsedHighlights = s.home_highlight_categories ? JSON.parse(s.home_highlight_categories) : null
        homeHighlightSettings.value = (parsedHighlights && typeof parsedHighlights === 'object') ? parsedHighlights : null
      } catch(e) {}


      try {
        const parsedFloatingCards = s.home_floating_cards ? JSON.parse(s.home_floating_cards) : []
        floatingCards.value = Array.isArray(parsedFloatingCards) && parsedFloatingCards.length > 0 ? parsedFloatingCards : defaultFloatingCards
      } catch(e) {
        floatingCards.value = defaultFloatingCards
      }
      homeShowFloatingCards.value = false

      try {
        const parsedHeroFeatures = s.home_hero_features_bar_items ? JSON.parse(s.home_hero_features_bar_items) : []
        heroFeaturesBarItems.value = Array.isArray(parsedHeroFeatures) && parsedHeroFeatures.length > 0 ? parsedHeroFeatures : defaultHeroFeaturesBarItems
      } catch(e) {
        heroFeaturesBarItems.value = defaultHeroFeaturesBarItems
      }
      homeShowHeroFeaturesBar.value = s.home_show_hero_features_bar !== 'false'
      try {
        const parsedTitles = s.home_section_titles ? JSON.parse(s.home_section_titles) : null
        if (parsedTitles && Object.keys(parsedTitles).length > 0) {
          sectionTitles.value = { ...sectionTitles.value, ...parsedTitles }
        } else {
          sectionTitles.value = { ...sectionTitles.value, ...defaultSectionTitles }
        }
      } catch(e) {
        sectionTitles.value = { ...sectionTitles.value, ...defaultSectionTitles }
      }

      featuresHeading.value = s.home_features_heading || 'GREENHOUSE SOLUTION'
      featuresTitle.value = s.home_features_title || 'โรงเรือนสำเร็จรูปเพื่อการเกษตรคุณภาพสูง'
      featuresDesc.value = s.home_features_desc || 'GreenHouse Solutions ผู้นำนวัตกรรมโรงเรือนสำเร็จรูปในประเทศไทย โครงสร้างพรีเมียมกัลวาไนซ์กันสนิม ช่วยปกป้องพืชผล ป้องกันแมลง และระบายอากาศให้เหมาะสมกับพืชแต่ละชนิด เพิ่มอัตราการรอดและคุณภาพของผลผลิตสูงสุด'
      const rawFeatImg = s.home_features_image || '/images/home/hero-slide-1.png'
      featuresImage.value = (rawFeatImg && !rawFeatImg.startsWith('http')) ? rawFeatImg : '/images/home/hero-slide-1.png'
      featuresBadgeTitle.value = s.home_features_badge_title || 'Greenhouse Innovation'
      featuresBadgeDesc.value = s.home_features_badge_desc || 'โรงเรือนมาตรฐานครบวงจร'



      // Load consultant card settings
      consultantAvatar.value = s.home_consultant_avatar || '/images/expert.png'
      consultantTitle.value = s.home_consultant_title || 'วางแผนติดตั้งโรงเรือน\nกับผู้เชี่ยวชาญตัวจริง'
      consultantDesc.value = s.home_consultant_desc || 'วิเคราะห์พื้นที่ ประเมินงบประมาณ และเลือกโรงเรือนที่เหมาะกับความต้องการของคุณ โดยไม่มีค่าใช้จ่าย'
      consultantCtaText.value = s.home_consultant_cta_text || 'นัดหมายปรึกษาฟรี'
      consultantCtaLink.value = s.home_consultant_cta_link || '/contact'
      try {
        const parsedBullets = s.home_consultant_bullets ? JSON.parse(s.home_consultant_bullets) : []
        consultantBullets.value = Array.isArray(parsedBullets) && parsedBullets.length > 0 ? parsedBullets : defaultConsultantBullets
      } catch (e) {
        consultantBullets.value = defaultConsultantBullets
      }

      // Section visibility toggles
      homeShowAffiliates.value = s.home_show_affiliates !== 'false'
      homeShowStats.value = s.home_show_stats !== 'false'
      homeShowFeatures.value = s.home_show_features !== 'false'
      
    }
  } catch (error) {
    console.error('Failed to load homepage settings:', error)
    slides.value = [...defaultSlides]
    stats.value = [...defaultStats]
    floatingCards.value = [...defaultFloatingCards]
    heroFeaturesBarItems.value = [...defaultHeroFeaturesBarItems]
    sectionTitles.value = { ...defaultSectionTitles }
    consultantBullets.value = [...defaultConsultantBullets]
  } finally {
    settingsLoading.value = false
    
    // Start slider with progress if we have slides
    if (slides.value.length > 1 && !slideInterval) {
      startSlideProgress()
    }
  }
}


const SLIDE_DURATION = 7000 // 7 seconds per slide
const PROGRESS_INTERVAL = 30 // update every 30ms for smooth animation

const startSlideProgress = () => {
  clearInterval(slideInterval)
  clearInterval(progressInterval)
  slideProgress.value = 100
}

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.value.length
  startSlideProgress()
}

const prevSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + slides.value.length) % slides.value.length
  startSlideProgress()
}

const goToSlide = (index) => {
  currentSlide.value = index
  startSlideProgress()
}

// Add Structured Data (JSON-LD)
const addStructuredData = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": settingsStore.storeName || '',
    "image": settingsStore.storeLogo || settingsStore.storeFavicon || '',
    "@id": settingsStore.storeUrl || window.location.origin,
    "url": settingsStore.storeUrl || window.location.origin,
    "telephone": settingsStore.storePhone || '',
    "address": {
      "@type": "PostalAddress",
      "streetAddress": settingsStore.storeAddress || '',
      "addressCountry": "TH"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
      ],
      "opens": "08:30",
      "closes": "17:30"
    }
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(schema)
  script.id = 'json-ld-localbusiness'
  document.head.appendChild(script)
}

const sectionTitles = ref({
  categoriesTitle: 'คอลเลกชัน',
  categoriesTitleHighlight: 'พื้นที่เก็บของ',
  categoriesDesc: 'เลือกดูหมวดหมู่สินค้าที่ออกแบบมาเพื่อตอบโจทย์ทุกพื้นที่ของคุณ',
  collectionTitle: 'รุ่นยอดนิยม',
  collectionDesc: 'สัมผัสการออกแบบที่ขายดีที่สุด การันตีคุณภาพและความสวยงามเกินราคา',
  projectsTitle: 'ผลงานของเรา',
  projectsHeading: 'ภาพผลงานติดตั้งจริงล่าสุด',
  testimonialsTitle: 'เสียงตอบรับจากลูกค้า',
  testimonialsHeading: 'ความไว้วางใจจากตัวจริง',
  partnersTitle: 'พาร์ทเนอร์ของเรา',
  partnersHeading: 'ความไว้วางใจจากองค์กรชั้นนำ',
  partnersDesc: 'เราได้รับความไว้วางใจจากองค์กรชั้นนำมากมาย ที่เลือกใช้บริการของเรา ในการยกระดับพื้นที่และเพิ่มประสิทธิภาพการจัดเก็บ',
  partnersMotto: 'เรามุ่งมั่นส่งมอบสินค้าคุณภาพและบริการที่เป็นเลิศ เพื่อสร้างความไว้วางใจและความพึงพอใจสูงสุดให้กับทุกองค์กร',
  articlesTitle: 'คลังสาระ',
  articlesHeading: 'อัพเดทข่าวสารน่ารู้',
  affiliatesTitle: 'บริษัทในเครือ',
  affiliatesHeading: 'เครือข่ายธุรกิจของเรา',
  affiliatesDesc: 'บริษัทในเครือที่ร่วมขับเคลื่อนธุรกิจด้วยนวัตกรรม คุณภาพ และความเชี่ยวชาญ เพื่อสร้างคุณค่าและเติบโตไปด้วยกันอย่างมั่นคง'
})



const highlightCatIndex = ref(0)

const highlightProgress = ref(0)
let highlightInterval = null

const HIGHLIGHT_DURATION = 6000
const HIGHLIGHT_TICK = 30

const highlightCategoryProducts = ref({}) // cache products for each highlighted category

const fetchHighlightProducts = async (catIndex) => {
  if (highlightCategoryProducts.value[catIndex]) return
  const highlightCat = highlightCategoriesList.value[catIndex];
  if (!highlightCat) return
  
  const selectedIds = highlightCat.productIds ? highlightCat.productIds.filter(id => id && String(id).trim() !== '').map(id => String(id).trim()) : [];
  
  if (selectedIds.length > 0) {
    try {
      const promises = selectedIds.map(id => 
        fetch(`/api/products/${id}`)
          .then(r => r.json())
          .catch(e => { console.warn(`Failed to fetch highlight product ID ${id}`, e); return null; })
      );
      const results = await Promise.all(promises);
      highlightCategoryProducts.value[catIndex] = results.filter(Boolean).map(r => r.data).filter(Boolean);
    } catch (err) {
      console.error('Failed to fetch specific highlight products', err)
    }
  } else {
    try {
      const res = await fetch(`/api/products?category=${encodeURIComponent(highlightCat.name)}&limit=3`)
      const data = await res.json()
      if (data.success) {
        highlightCategoryProducts.value[catIndex] = data.data
      }
    } catch (err) {
      console.error('Failed to fetch highlight products', err)
    }
  }
}

watch(highlightCatIndex, (newIdx) => {
  fetchHighlightProducts(newIdx)
})

const startHighlightRotation = () => {
  clearInterval(highlightInterval)
  highlightProgress.value = 0
  highlightInterval = setInterval(() => {
    highlightProgress.value += (HIGHLIGHT_TICK / HIGHLIGHT_DURATION) * 100
    if (highlightProgress.value >= 100) {
      highlightProgress.value = 0
      highlightCatIndex.value = (highlightCatIndex.value + 1) % (highlightCategoriesList.value.length || 1) // Rotate through available categories
    }
  }, HIGHLIGHT_TICK)
}

const setHighlightCat = (index) => {
  highlightCatIndex.value = index
  startHighlightRotation()
}

const productSliderRef = ref(null)

const scrollProductsLeft = () => {
  if (productSliderRef.value) {
    productSliderRef.value.scrollBy({ left: -240, behavior: 'smooth' })
  }
}

const scrollProductsRight = () => {
  if (productSliderRef.value) {
    productSliderRef.value.scrollBy({ left: 240, behavior: 'smooth' })
  }
}

const activeCategoryFeatures = computed(() => {
  const currentCat = highlightCategoriesList.value[highlightCatIndex.value];
  if (!currentCat) return [];

  let parsedFeatures = { enabled: true, items: [] };
  try {
    if (currentCat.features) {
      if (typeof currentCat.features === 'string') {
        const parsed = JSON.parse(currentCat.features);
        if (Array.isArray(parsed)) {
          parsedFeatures.items = parsed;
        } else if (parsed && typeof parsed === 'object') {
          parsedFeatures.enabled = parsed.enabled !== false;
          parsedFeatures.items = Array.isArray(parsed.items) ? parsed.items : [];
        }
      } else if (Array.isArray(currentCat.features)) {
        parsedFeatures.items = currentCat.features;
      } else if (currentCat.features && typeof currentCat.features === 'object') {
        parsedFeatures.enabled = currentCat.features.enabled !== false;
        parsedFeatures.items = Array.isArray(currentCat.features.items) ? currentCat.features.items : [];
      }
    }
  } catch (e) {
    console.error('Error parsing category features:', e);
  }

  if (parsedFeatures.enabled && parsedFeatures.items && parsedFeatures.items.length > 0) {
    const items = parsedFeatures.items.filter(f => f && (f.name || f.title));
    if (items.length > 0) {
      return items.map(f => ({
        icon: f.icon || 'check',
        title: f.name || f.title || '',
        desc: f.description || f.desc || ''
      }));
    }
  }

  // Fallback to hardcoded features
  const currentCatName = currentCat.name || '';
  if (currentCatName.includes('พลาสติก PP')) {
    return [
      { icon: 'shield', title: 'แข็งแรง ทนทาน', desc: 'พลาสติก PP เกรดพรีเมียม นำเข้าจากอเมริกา' },
      { icon: 'sun', title: 'ทนทานต่อรังสียูวี', desc: 'ไม่ซีดจาง ไม่กรอบแตกง่าย ปกป้องแดดแรงได้ดี' },
      { icon: 'rain', title: 'กันน้ำ 100%', desc: 'ซีลกันฝนสาด ลมแรง ป้องกันความชื้นเข้าด้านใน' },
      { icon: 'tools', title: 'ประกอบง่าย', desc: 'ระบบน็อคดาวน์สำเร็จรูป ติดตั้งง่ายในวันเดียว' }
    ];
  } else if (currentCatName.includes('พลาสติก HDPE')) {
    return [
      { icon: 'shield', title: 'ทนทานพิเศษ', desc: 'พลาสติก HDPE หนาพิเศษ ผิวลายไม้พรีเมียม' },
      { icon: 'sun', title: 'ทนทานแดดฝน', desc: 'ไม่บิดเบี้ยว ทนต่อทุกสภาพอากาศ ทนต่อฝนกรด' },
      { icon: 'rain', title: 'โครงสร้างสองชั้น', desc: 'ผนังหนาพิเศษ รับแรงกระแทกและน้ำฝนได้ดี' },
      { icon: 'medal', title: 'รับประกันยาวนาน', desc: 'รับประกันโครงสร้างยาวนาน 10 ปี มั่นใจสูงสุด' }
    ];
  } else if (currentCatName.includes('โลหะ')) {
    return [
      { icon: 'shield', title: 'โครงเหล็กกันสนิม', desc: 'เหล็ก Galvalume USA อบสีความร้อน 4 ชั้น' },
      { icon: 'wind', title: 'ต้านทานลมพายุ', desc: 'ผ่านการทดสอบต้านทานแรงลมได้ถึง 120 กม./ชม.' },
      { icon: 'lock', title: 'ระบบล็อกหนาแน่น', desc: 'ประตูติดตั้งระบบล็อกแบบนิรภัยเพื่อป้องกันความปลอดภัย' },
      { icon: 'star', title: 'ทนทานไร้สนิม', desc: 'รับประกันสนิมและโครงสร้างยาวนาน 10 ปีเต็ม' }
    ];
  } else if (currentCatName.includes('โรงเรือน') || currentCatName.includes('กรีนเฮาส์')) {
    return [
      { icon: 'sun', title: 'ควบคุมสภาวะภายใน', desc: 'ออกแบบช่องระบายอากาศและผ้ามุ้งกรองแสงอย่างเหมาะสม' },
      { icon: 'sun', title: 'พลาสติกกรองแสง UV', desc: 'ฟิล์มคลุมโรงเรือนคุณภาพสูง กรองแสงสม่ำเสมอ' },
      { icon: 'wind', title: 'ช่องระบายอากาศ', desc: 'มุ้งตาข่ายกันแมลงรอบทิศทาง ป้องกันโรคพืชระบาด' },
      { icon: 'tools', title: 'โครงสร้างพรีเมียม', desc: 'เหล็กชุบกัลวาไนซ์กันสนิมอย่างหนา แข็งแรงทนทาน' }
    ];
  } else {
    return [
      { icon: 'shield', title: 'คัดสรรวัสดุพรีเมียม', desc: 'วัสดุคุณภาพสูง ทนทาน ตอบโจทย์ทุกการใช้สอย' },
      { icon: 'sun', title: 'ใช้งานกลางแจ้ง', desc: 'ออกแบบมาเพื่อทนต่อรังสียูวีและทนทานทุกฤดูกาล' },
      { icon: 'star', title: 'ดีไซน์พรีเมียม', desc: 'รูปทรงโมเดิร์นมินิมอล ยกระดับความงามให้พื้นที่' },
      { icon: 'thumbs-up', title: 'รับประกันยาวนาน', desc: 'รับประกันความคุ้มค่า โครงสร้างแข็งแรงไม่ผิดหวัง' }
    ];
  }
})

onMounted(() => {
  if (categories.value.length > 0) {
    fetchHighlightProducts(0)
  }
  startHighlightRotation()
  if (typeof document !== 'undefined') {
    document.addEventListener('click', closeContactDropdown)
  }
})

onUnmounted(() => {
  clearInterval(highlightInterval)
  stopPartnerAutoplay()
  stopAutoScrollCat()
  stopArticleAutoplay()
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWidth)
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('click', closeContactDropdown)
  }
})



// Affiliated companies (loaded from settings)
const affiliatedCompanies = ref([])

const getAffiliateIconName = (company) => {
  if (!company) return 'globe';
  if (company.icon) return company.icon;
  const name = (company.name || '').toLowerCase();
  if (name.includes('crtech')) return 'lightning';
  if (name.includes('autosplice')) return 'chip';
  if (name.includes('morespace')) return 'home';
  return 'globe';
}

const fetchRecentContent = async () => {
  try {
    const [projRes, artRes] = await Promise.all([
      fetch('/api/projects/published?limit=10'),
      fetch('/api/articles/published?limit=9')
    ])
    
    const projData = await projRes.json()
    const artData = await artRes.json()
    
    if (projData.success && Array.isArray(projData.data) && projData.data.length >= 5) {
      recentProjects.value = projData.data
    } else {
      recentProjects.value = []
    }
    
    if (artData.success && artData.data && artData.data.length > 0) {
      recentArticles.value = artData.data
    } else {
      recentArticles.value = [
        {
          id: 1,
          title: 'คู่มือเลือกโรงเรือนปลูกพืชระดับมืออาชีพ: เจาะลึกสเปก MS-GH004',
          slug: 'professional-greenhouse-selection-guide-ms-gh004',
          excerpt: 'คู่มือการเลือกโรงเรือนสำหรับผู้เริ่มต้นและมืออาชีพ เจาะลึกโครงสร้างและขนาดที่เหมาะสมเพื่อเพิ่มผลผลิตอย่างยั่งยืน',
          cover_image: '/images/home/hero-slide-1.png',
          created_at: '2026-06-01T08:00:00.000Z'
        },
        {
          id: 2,
          title: 'เจาะลึกโรงเรือนปลูกพืชรุ่น MS-GG33: พื้นที่ 9 ตร.ม. สวรรค์ของคนรักสวน',
          slug: 'deep-dive-greenhouse-ms-gg33-gardener-paradise',
          excerpt: 'พาไปดูโรงเรือนรุ่นขายดี MS-GG33 ขนาดกำลังดี เหมาะสำหรับพืชผักสวนครัวและดอกไม้ พร้อมระบบระบายอากาศที่ดีเยี่ยม',
          cover_image: '/images/home/hero-slide-2.png',
          created_at: '2026-05-23T08:00:00.000Z'
        },
        {
          id: 3,
          title: 'ไขความลับ \'กระถางผ้า\': ทำไมมือโปรเลือกใช้? เจาะลึกรุ่น MS-fabric',
          slug: 'fabric-pots-secrets-why-pros-choose-ms-fabric',
          excerpt: 'ไขข้อสงสัยทำไมกระถางผ้าถึงช่วยให้รากพืชเดินได้ดีกว่ากระถางพลาสติกทั่วไป พร้อมเจาะลึกวัสดุและการระบายน้ำที่ดีเยี่ยม',
          cover_image: '/images/home/hero-slide-3.png',
          created_at: '2026-05-21T08:00:00.000Z'
        },
        {
          id: 4,
          title: '5 เทคนิคการควบคุมอุณหภูมิในโรงเรือนอัจฉริยะสำหรับฤดูร้อน',
          slug: '5-tips-temperature-control-smart-greenhouse-summer',
          excerpt: 'แนะนำวิธีการจัดการความร้อนในโรงเรือนพืชอย่างมีประสิทธิภาพด้วยระบบพ่นหมอกและพัดลมระบายอากาศอัตโนมัติ',
          cover_image: '/images/home/hero-slide-1.png',
          created_at: '2026-05-15T08:00:00.000Z'
        },
        {
          id: 5,
          title: 'การเลือกวัสดุคลุมโรงเรือน: มุ้งกันแมลงและพลาสติกกัน UV แบบไหนดีที่สุด?',
          slug: 'greenhouse-covering-materials-insects-net-uv-plastic',
          excerpt: 'เปรียบเทียบข้อดีข้อเสียของมุ้งกันแมลงแต่ละขนาดตา และพลาสติก PE กันแสงยูวี เพื่อปกป้องผลผลิตของคุณให้ปลอดภัย',
          cover_image: '/images/home/hero-slide-2.png',
          created_at: '2026-05-10T08:00:00.000Z'
        },
        {
          id: 6,
          title: 'ไอเดียจัดระเบียบชั้นวางของในโรงเรือนสำหรับพื้นที่ขนาดเล็ก',
          slug: 'organizing-shelves-ideas-small-space-greenhouse',
          excerpt: 'เปลี่ยนพื้นที่จำกัดในโรงเรือนให้ใช้งานได้เต็มประสิทธิภาพด้วยชั้นวางเหล็กและกล่องจัดเก็บแบบแยกหมวดหมู่',
          cover_image: '/images/home/hero-slide-3.png',
          created_at: '2026-05-05T08:00:00.000Z'
        },
        {
          id: 7,
          title: 'เทรนด์การปลูกผักออร์แกนิกในโรงเรือนยุค 2026',
          slug: 'organic-greenhouse-vegetable-trends-2026',
          excerpt: 'เจาะเทรนด์การบริโภคผักปลอดภัยและการใช้เทคโนโลยีโรงเรือนประหยัดพลังงานเพื่อการปลูกผักสลัดแบบออร์แกนิก',
          cover_image: '/images/home/hero-slide-1.png',
          created_at: '2026-05-01T08:00:00.000Z'
        },
        {
          id: 8,
          title: 'วิธีการดูแลรักษามุ้งกันแมลงในโรงเรือนให้ใช้งานได้ยาวนาน',
          slug: 'how-to-maintain-anti-insect-nets-greenhouse',
          excerpt: 'เคล็ดลับการล้างทำความสะอาดมุ้งกันแมลงและตรวจเช็กรูฉีกขาดเพื่อป้องกันศัตรูพืชเล็ดลอดเข้าทำลายผลผลิต',
          cover_image: '/images/home/hero-slide-2.png',
          created_at: '2026-04-25T08:00:00.000Z'
        },
        {
          id: 9,
          title: 'การเลือกสีพลาสติกคลุมโรงเรือน: ใส หรือ ขาวขุ่น แบบไหนดีต่อพืชของคุณ?',
          slug: 'greenhouse-pe-film-clear-vs-diffused-white',
          excerpt: 'วิเคราะห์ความแตกต่างของปริมาณแสงที่ส่องผ่านพลาสติกคลุมโรงเรือนแบบใสและขาวขุ่นที่มีผลต่อการสังเคราะห์แสงของพืช',
          cover_image: '/images/home/hero-slide-3.png',
          created_at: '2026-04-20T08:00:00.000Z'
        }
      ]
    }
    startArticleAutoplay()
  } catch (err) {
    console.error('Failed to fetch recent content', err)
  }
}

const fetchCategories = async () => {
  try {
    const res = await fetch('/api/categories')
    const data = await res.json()
    if (data.success) {
      const priority = [
        'บ้านเก็บของพลาสติก PP',
        'บ้านเก็บของพลาสติก HDPE',
        'บ้านเก็บของโลหะ',
        'โรงเรือนปลูกพืช',
        'กล่องเก็บของกลางแจ้ง'
      ];
      // Sort categories: Priority first (in exact order), then the rest
      const sorted = data.data.sort((a, b) => {
        const indexA = priority.indexOf(a.name);
        const indexB = priority.indexOf(b.name);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });
      categories.value = sorted;

      // Initialize highlight Categories list
      if (homeHighlightSettings.value && homeHighlightSettings.value.items && homeHighlightSettings.value.items.some(i => i.categoryId)) {
        const configuredCats = homeHighlightSettings.value.items
          .map(item => {
            const cat = categories.value.find(c => String(c.id) === String(item.categoryId));
            if (!cat) return null;
            return {
              ...cat,
              customDescription: item.customDesc || cat.description,
              productIds: item.productIds || [],
              tags: item.tags || ['โมเดิร์น', 'พื้นที่กว้าง', 'อเนกประสงค์'],
              badgeText: item.badgeText || 'PREMIUM',
              badgeStars: item.badgeStars !== undefined ? item.badgeStars : 5,
              badgeIcon: item.badgeIcon || 'diamond',
              showBadge: item.showBadge !== false
            }
          })
          .filter(Boolean);
          
        const usedIds = configuredCats.map(c => String(c.id));
        let fallbackIndex = 0;
        while (configuredCats.length < 5 && fallbackIndex < categories.value.length) {
          const fallbackCat = categories.value[fallbackIndex];
          if (!usedIds.includes(String(fallbackCat.id))) {
            configuredCats.push({
              ...fallbackCat,
              customDescription: fallbackCat.description,
              productIds: [],
              tags: ['โมเดิร์น', 'พื้นที่กว้าง', 'อเนกประสงค์'],
              badgeText: 'PREMIUM',
              badgeStars: 5,
              badgeIcon: 'diamond',
              showBadge: true
            });
            usedIds.push(String(fallbackCat.id));
          }
          fallbackIndex++;
        }
        highlightCategoriesList.value = configuredCats;
      } else {
        // Fallback to top 5 categories
        highlightCategoriesList.value = categories.value.slice(0, 5).map(cat => ({
          ...cat,
          customDescription: cat.description,
          productIds: [],
          tags: ['โมเดิร์น', 'พื้นที่กว้าง', 'อเนกประสงค์'],
          badgeText: 'PREMIUM',
          badgeStars: 5,
          badgeIcon: 'diamond',
          showBadge: true
        }));
      }

      fetchHighlightProducts(0);

      // Center the slider and start auto-scroll after DOM renders
      nextTick(() => {
        if (categorySliderRef.value && extendedCategories.value.length > 0) {
          const container = categorySliderRef.value
          container.style.scrollBehavior = 'auto'
          
          const jumpIndex = Math.floor(extendedCategories.value.length / 4)
          const children = Array.from(container.children)
          if (children[jumpIndex]) {
            const targetChild = children[jumpIndex]
            const scrollTarget = targetChild.offsetLeft - (container.clientWidth / 2) + (targetChild.clientWidth / 2)
            container.scrollLeft = scrollTarget
          }
          
          container.style.scrollBehavior = 'smooth'
          startAutoScrollCat()
        }
      })
    }
  } catch (err) {
    console.error('Failed to fetch categories', err)
  }
}

const buildShowcaseCategoriesData = async () => {
  if (categoryShowcaseSettings.value.length === 0 || categories.value.length === 0) return;
  
  const formattedData = [];
  
  for (const showcase of categoryShowcaseSettings.value) {
    const category = categories.value.find(c => String(c.id) === String(showcase.categoryId));
    if (!category) continue;
    
    // Fetch specifically the products selected for this category
    let products = [];
    if (showcase.productIds && showcase.productIds.length > 0) {
      try {
        const res = await fetch(`/api/products?category=${encodeURIComponent(category.name)}`);
        const data = await res.json();
        if (data.success) {
           products = data.data.filter(p => showcase.productIds.includes(p.id))
                              .sort((a,b) => showcase.productIds.indexOf(a.id) - showcase.productIds.indexOf(b.id));
        }
      } catch(e) { console.error('Error fetching showcase products for category', category.name, e) }
    }
    
    // Fallback: if no specific products were selected, or if the selected products were deleted
    if (products.length === 0) {
      try {
        const res = await fetch(`/api/products?category=${encodeURIComponent(category.name)}`);
        const data = await res.json();
        // Since API limit might not be supported, enforce local slice
        if (data.success) products = data.data.slice(0, 4); // Show 4 latest products as fallback
      } catch(e) { console.error('Error fetching showcase products fallback for category', category.name, e) }
    }
    
    
    let parsedFeatures = { enabled: true, items: [] };
    try {
      if (typeof category.features === 'string') {
        const parsed = JSON.parse(category.features);
        if (Array.isArray(parsed)) {
          parsedFeatures.items = parsed || [];
        } else if (parsed && typeof parsed === 'object') {
          parsedFeatures.enabled = parsed.enabled !== false;
          parsedFeatures.items = Array.isArray(parsed.items) ? parsed.items : [];
        }
      } else if (Array.isArray(category.features)) {
        parsedFeatures.items = [...category.features];
      } else if (category.features && typeof category.features === 'object') {
        parsedFeatures.enabled = category.features.enabled !== false;
        parsedFeatures.items = Array.isArray(category.features.items) ? category.features.items : [];
      }
    } catch (e) {}

    if (products.length > 0) {
      formattedData.push({
        category: { ...category, features: parsedFeatures },
        products: products.map(p => ({
          id: p.id,
          slug: p.slug,
          sku: p.sku,
          title: p.name,
          image: p.image_url || '/images/placeholder.png',
          subtitle: p.sku ? `SKU: ${p.sku}` : (p.category || 'Premium Unit'),
          price: p.price,
          originalPrice: p.original_price,
          is_out_of_stock: p.is_out_of_stock || false,
          card_features: p.card_features || null,
          compare_enabled: p.compare_enabled !== false
        }))
      });
    }
  }
  
  showcaseCategoriesData.value = formattedData;
}

// --- Category Flow Slider Logic ---
const categorySliderRef = ref(null)
const activeCenterIndex = ref(0)
let autoScrollCatInterval = null
const isHoveringCatSlider = ref(false)

const extendedCategories = computed(() => {
  if (!categories.value || categories.value.length === 0) return []
  // Duplicate a few times for infinite looping
  return [...categories.value, ...categories.value, ...categories.value, ...categories.value]
})

const updateActiveCenter = () => {
  if (!categorySliderRef.value) return
  const container = categorySliderRef.value
  const center = container.scrollLeft + container.clientWidth / 2
  
  let closestIndex = 0
  let minDistance = Infinity
  
  const children = Array.from(container.children)
  children.forEach((child, index) => {
    const childCenter = child.offsetLeft + child.clientWidth / 2 - container.offsetLeft
    // The exact child center relative to scroll container
    const absDistance = Math.abs(childCenter - center)
    if (absDistance < minDistance) {
      minDistance = absDistance
      closestIndex = index
    }
  })
  
  activeCenterIndex.value = closestIndex
}

const slideToNextCat = () => {
  if (!categorySliderRef.value) return
  const container = categorySliderRef.value
  const children = Array.from(container.children)
  
  let nextIndex = activeCenterIndex.value + 1
  
  // Infinite scroll illusion reset
  if (nextIndex >= Math.floor(extendedCategories.value.length * 0.75)) {
    // Silently jump back
    container.style.scrollBehavior = 'auto'
    const resetIndex = Math.floor(extendedCategories.value.length / 4)
    const resetChild = children[resetIndex]
    const resetScrollTarget = resetChild.offsetLeft - (container.clientWidth / 2) + (resetChild.clientWidth / 2)
    container.scrollLeft = resetScrollTarget
    
    // Request reflow
    void container.offsetWidth
    
    container.style.scrollBehavior = 'smooth'
    nextIndex = resetIndex + 1
  }

  if (nextIndex < children.length) {
    const nextChild = children[nextIndex]
    const scrollTarget = nextChild.offsetLeft - (container.clientWidth / 2) + (nextChild.clientWidth / 2)
    
    container.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    })
  }
}

const startAutoScrollCat = () => {
  if (autoScrollCatInterval) clearInterval(autoScrollCatInterval)
  autoScrollCatInterval = setInterval(() => {
    if (!isHoveringCatSlider.value) {
      slideToNextCat()
    }
  }, 2500) // Slide interval
}

const stopAutoScrollCat = () => {
  if (autoScrollCatInterval) clearInterval(autoScrollCatInterval)
}

const onCatSliderEnter = () => {
  isHoveringCatSlider.value = true
}

const onCatSliderLeave = () => {
  isHoveringCatSlider.value = false
}

const onCatScroll = () => {
  // Update active index via requestAnimationFrame to throttle
  window.requestAnimationFrame(() => {
    updateActiveCenter()
  })
}

const slideCatByDirection = (direction) => {
  if (!categorySliderRef.value) return
  const container = categorySliderRef.value
  const children = Array.from(container.children)
  
  let nextIndex = activeCenterIndex.value + direction
  if (nextIndex < 0) nextIndex = 0
  if (nextIndex >= children.length) nextIndex = children.length - 1
  
  const targetChild = children[nextIndex]
  if (targetChild) {
    const scrollTarget = targetChild.offsetLeft - (container.clientWidth / 2) + (targetChild.clientWidth / 2)
    container.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    })
  }
}

const prevCatSlide = () => {
  slideCatByDirection(-1)
  startAutoScrollCat()
}
const nextCatSlide = () => {
  slideCatByDirection(1)
  startAutoScrollCat()
}

const handleCatClick = (e, index) => {
  if (index !== activeCenterIndex.value) {
    e.preventDefault()
    // Scroll to it instead of navigating immediately if it's not the center
    const container = categorySliderRef.value
    const child = container.children[index]
    const scrollTarget = child.offsetLeft - (container.clientWidth / 2) + (child.clientWidth / 2)
    
    container.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    })
  }
}

const getCategoryDisplayName = (name) => {
  if (name === 'บ้านเก็บของพลาสติก PP') {
    return { title: 'บ้านเก็บของพลาสติก', sub: 'PP' }
  }
  if (name === 'บ้านเก็บของพลาสติก HDPE') {
    return { title: 'บ้านเก็บของพลาสติก', sub: 'HDPE' }
  }
  if (name === 'บ้านเก็บของโลหะ') {
    return { title: 'บ้านเก็บของโลหะ', sub: 'METAL SHED' }
  }
  if (name === 'โรงเรือนปลูกพืช') {
    return { title: 'โรงเรือนปลูกพืช', sub: '' }
  }
  if (name === 'กล่องเก็บของกลางแจ้ง' || name === 'ตู้และกล่องเก็บของกลางแจ้ง') {
    return { title: 'ตู้และกล่องเก็บของ', sub: 'กลางแจ้ง' }
  }
  return { title: name, sub: '' }
}

const activeCategoryOriginalIndex = computed(() => {
  if (!categories.value || categories.value.length === 0) return 0
  return activeCenterIndex.value % categories.value.length
})

const scrollToCategoryIndex = (originalIndex) => {
  if (!categorySliderRef.value || categories.value.length === 0) return
  const container = categorySliderRef.value
  const children = Array.from(container.children)
  const targetIndex = categories.value.length + originalIndex
  const targetChild = children[targetIndex]
  if (targetChild) {
    const scrollTarget = targetChild.offsetLeft - (container.clientWidth / 2) + (targetChild.clientWidth / 2)
    container.scrollTo({
      left: scrollTarget,
      behavior: 'smooth'
    })
    startAutoScrollCat()
  }
}



const initData = async () => {
  await loadHomepageSettings()
  addStructuredData()
  fetchRecentContent()
  await fetchCategories()
  await buildShowcaseCategoriesData()
  
  if (categories.value.length > 0) {
    fetchHighlightProducts(0)
  }
  startHighlightRotation()
  startPartnerAutoplay()
}

const handleScroll = () => {
  // Example: You can add scroll-based animations or lazy loading here
  // For now, it's just a placeholder for the event listener
}

onMounted(async () => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('resize', updateWidth)
  initData()
  if (authStore.isAuthenticated) {
    wishlistStore.fetchWishlist()
  }
  
  // Refresh AOS after content loads
  await nextTick()
})


</script>

<template>
  <div class="home-page overflow-x-hidden bg-white dark:bg-[#0a0f16]">
    
    <!-- Loading State -->
    <div v-if="settingsLoading" class="min-h-screen flex flex-col items-center justify-center bg-[#0a0f16]">
      <div class="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mb-4"></div>
      <p class="text-emerald-400 text-sm font-bold tracking-widest uppercase">กำลังโหลดหน้าเว็บไซต์...</p>
    </div>

    <!-- Main Content -->
    <template v-else>
        <section ref="heroRef" class="relative min-h-[90vh] md:min-h-screen flex items-center justify-center bg-[#f4faf6] dark:bg-[#0a0f16] overflow-hidden">
      
      <!-- Animated Tech Grid Overlay -->
      <div class="absolute inset-0 z-[2] pointer-events-none opacity-[0.02]">
        <div class="absolute inset-0 hero-grid"></div>
      </div>

      <!-- Film Grain Overlay -->
      <div class="absolute inset-0 z-[5] opacity-[0.02] pointer-events-none mix-blend-overlay" style="background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 256 256%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E');"></div>

      <!-- Slide Images -->
      <transition-group name="slide-fade" tag="div" class="absolute inset-0 z-0 overflow-hidden">
        <div v-for="(slide, index) in slides" :key="index" v-show="currentSlide === index" class="absolute inset-0 w-full h-full">
          <img width="1920" height="1080" class="w-full h-full object-cover opacity-90 dark:opacity-40 filter brightness-95 dark:brightness-50 origin-center" :src="getOptimizedImageUrl(slide.image, 1200)" :alt="slide.titleLine1" :fetchpriority="index === 0 ? 'high' : 'auto'" loading="eager">
          <!-- Subtle Vignette / Overlays -->
          <div class="absolute inset-0 bg-gradient-to-t from-[#0a0f16]/15 via-transparent to-transparent"></div>
        </div>
      </transition-group>

      <!-- Premium Gradient Overlay Mask (Replaces the curve SVG) -->
      <!-- Desktop Gradient overlay (left to right) -->
      <div class="absolute inset-y-0 left-0 w-full lg:w-[60%] bg-gradient-to-r from-[#f4faf6] via-[#f4faf6]/95 to-transparent dark:from-[#0a0f16] dark:via-[#0a0f16]/95 dark:to-transparent z-10 pointer-events-none hidden lg:block transition-all duration-500"></div>
      <!-- Mobile Gradient overlay (bottom to top + solid wash) -->
      <div class="absolute inset-0 bg-gradient-to-t from-[#f4faf6] via-[#f4faf6]/95 to-[#f4faf6]/85 dark:from-[#0a0f16] dark:via-[#0a0f16]/95 dark:to-[#0a0f16]/85 z-10 pointer-events-none lg:hidden transition-all duration-500"></div>

      <!-- Ambient Glow Orbs -->
      <div class="absolute top-[10%] left-[20%] w-96 h-96 rounded-full bg-emerald-400/10 dark:bg-emerald-500/5 blur-[120px] pointer-events-none z-10"></div>
      <div class="absolute bottom-[15%] right-[25%] w-80 h-80 rounded-full bg-teal-400/10 dark:bg-teal-500/5 blur-[100px] pointer-events-none z-10"></div>

      <!-- Main Content Container -->
      <div class="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28 md:pt-32 flex items-center h-full">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center w-full">
          <!-- Left side: Slides details (span 7) -->
          <div class="lg:col-span-7 z-20">
            <!-- Content Changes based on Slide (with staggered animations) -->
            <transition name="hero-stagger" mode="out-in">
              <div :key="currentSlide" class="w-full" v-if="slides && slides.length > 0 && slides[currentSlide]">
                <!-- Tag Badge with Pulse Dot -->
                <div class="inline-flex items-center gap-2.5 px-6 py-2 rounded-full bg-white/80 dark:bg-white/5 backdrop-blur-md border border-emerald-500/20 dark:border-white/10 mb-6 hero-stagger-1 shadow-sm hover:border-emerald-500/40 transition-all duration-300">
                  <span class="relative flex h-2 w-2">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span class="text-[11px] font-black tracking-widest text-slate-800 dark:text-emerald-400 uppercase font-sans">{{ slides[currentSlide]?.tag || '' }}</span>
                </div>
                
                <!-- Title with Premium Typography & Gradient text -->
                <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-[46px] xl:text-[52px] font-black text-slate-900 dark:text-white tracking-tight leading-[1.5] mb-6 hero-stagger-2">
                  {{ slides[currentSlide]?.titleLine1 || '' }} <br class="hidden sm:inline" />
                  <span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-700 via-teal-600 to-emerald-600 dark:from-emerald-400 dark:via-teal-400 dark:to-emerald-300">
                    {{ slides[currentSlide]?.titleLine2 || '' }}
                  </span>
                  <span class="text-[#c5a86d] dark:text-amber-400 ml-2 drop-shadow-sm">
                    {{ slides[currentSlide]?.titleHighlight || '' }}
                  </span>
                </h1>
                
                <!-- Subtitle / Description with improved legibility -->
                <p class="text-base text-slate-700 dark:text-gray-300 font-medium leading-relaxed mb-8 md:mb-10 max-w-xl hero-stagger-3">
                  {{ slides[currentSlide]?.desc || '' }}
                </p>
                
                <!-- Buttons as Elegant Pills (with shine/hover) -->
                <div class="flex flex-col sm:flex-row gap-4 hero-stagger-4">
                  <router-link :to="slides[currentSlide]?.ctaLink || '/products'" @click="trackCTA(slides[currentSlide]?.ctaAction)" class="group relative inline-flex items-center justify-center px-8 py-3.5 text-sm font-bold tracking-wider text-white transition-all bg-gradient-to-r from-emerald-700 to-teal-700 hover:from-emerald-600 hover:to-teal-600 dark:from-emerald-500 dark:to-teal-500 dark:hover:from-emerald-400 dark:hover:to-teal-400 rounded-full overflow-hidden shadow-lg hover:shadow-emerald-500/20 hover:-translate-y-0.5 transform active:scale-95 duration-300">
                    <span class="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"></span>
                    <span class="relative z-10 flex items-center gap-2">
                      <svg class="w-4 h-4 text-emerald-100 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 3.5 1 9.8a7 7 0 0 1-9 8.2Z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 22v-4h4"/>
                      </svg>
                      <span>{{ slides[currentSlide]?.ctaText || 'ดูสินค้า' }}</span>
                    </span>
                    <svg class="w-4 h-4 ml-2 transform group-hover:translate-x-1.5 transition-transform duration-300 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                    </svg>
                  </router-link>
                  
                  <router-link to="/space-calculator" @click="trackCTA('consult_ai')" v-if="slides[currentSlide]?.ctaLink !== '/space-calculator'" class="group relative inline-flex items-center justify-center px-8 py-3.5 text-sm font-bold tracking-wider text-slate-900 dark:text-white transition-all bg-white/70 dark:bg-white/5 backdrop-blur-md border border-emerald-600/20 dark:border-white/10 rounded-full hover:bg-emerald-50 dark:hover:bg-white/10 hover:border-emerald-500/40 dark:hover:border-emerald-400/40 shadow-md hover:shadow-lg hover:-translate-y-0.5 transform active:scale-95 duration-300 gap-2">
                    <span class="absolute inset-0 rounded-full border border-transparent bg-gradient-to-r from-emerald-500/0 via-emerald-500/20 to-emerald-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></span>
                    <span class="text-amber-600 dark:text-amber-400 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300 flex items-center">
                      <svg class="w-4 h-4 text-amber-600 dark:text-amber-400 fill-amber-500/10" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l2.4 7.2L22 10l-6 4.8 2.4 7.2-6.4-4.8-6.4 4.8 2.4-7.2-6-4.8 7.6-.8L12 2z"/>
                      </svg>
                    </span>
                    <span class="relative z-10">คำนวณพื้นที่จัดวางฟรี</span>
                  </router-link>
                </div>

                <!-- Slide Navigation and Progress Bar -->
                <div class="flex items-center gap-6 mt-10 lg:mt-14 select-none hero-stagger-4">
                  <!-- Slide Counter -->
                  <div class="text-slate-800 dark:text-white/60 text-xs font-bold tracking-wider font-mono">
                    <span class="text-lg font-black text-slate-900 dark:text-white">{{ String(currentSlide + 1).padStart(2, '0') }}</span>
                    <span class="opacity-30 px-2 text-sm">/</span>
                    <span class="opacity-50 text-xs">{{ String(slides.length).padStart(2, '0') }}</span>
                  </div>
                  
                  <!-- Progress Bars -->
                  <div class="flex gap-2 items-center">
                    <button v-for="(slide, index) in slides" :key="index" @click="goToSlide(index)" class="relative h-1 rounded-full transition-all duration-300 overflow-hidden" :class="currentSlide === index ? 'w-12 bg-emerald-600/10 dark:bg-emerald-400/10' : 'w-5 bg-slate-300/40 dark:bg-white/10 hover:bg-emerald-600/20'" :aria-label="'Go to slide ' + (index + 1)">
                      <span class="sr-only">Go to slide {{ index + 1 }}</span>
                      <div v-if="currentSlide === index" class="absolute inset-y-0 left-0 rounded-full bg-emerald-600 dark:bg-emerald-400 transition-none" :style="{ width: slideProgress + '%' }"></div>
                    </button>
                    <button @click="prevSlide" aria-label="สไลด์ก่อนหน้า" class="w-9 h-9 rounded-full border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-white/5 backdrop-blur-md hover:bg-emerald-600 hover:text-white dark:hover:bg-emerald-500 hover:border-emerald-600 dark:hover:border-emerald-500 text-slate-800 dark:text-white flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10 active:scale-90">
                      <span class="sr-only">สไลด์ก่อนหน้า</span>
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"></path></svg>
                    </button>
                    <button @click="nextSlide" aria-label="สไลด์ถัดไป" class="w-9 h-9 rounded-full border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-white/5 backdrop-blur-md hover:bg-emerald-600 hover:text-white dark:hover:bg-emerald-500 hover:border-emerald-600 dark:hover:border-emerald-500 text-slate-800 dark:text-white flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10 active:scale-90">
                      <span class="sr-only">สไลด์ถัดไป</span>
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Floating Glassmorphism SaaS/IoT Dashboard Cards (Right Side) -->
      <div v-if="homeShowFloatingCards && floatingCards.length > 0">
        <!-- Card 1 -->
        <div v-if="floatingCards[0]" class="hidden lg:block absolute top-[18%] right-[6%] lg:right-[8%] z-20 pointer-events-auto">
          <div class="relative backdrop-blur-xl bg-white/70 dark:bg-[#0a0f16]/75 p-5 rounded-2xl shadow-2xl border border-white/30 dark:border-white/10 flex flex-col gap-3 w-[260px] transition-all duration-300 hover:border-emerald-500/30">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <div class="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-700 dark:text-emerald-400">
                  <FeatureIcon :name="floatingCards[0].icon || 'chart'" class-name="w-4 h-4" />
                </div>
                <span class="text-xs font-bold text-slate-900 dark:text-slate-200">{{ floatingCards[0].title }}</span>
              </div>
              <span v-if="floatingCards[0].badgeText" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 uppercase">
                <span class="relative flex h-1.5 w-1.5">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </span>
                {{ floatingCards[0].badgeText }}
              </span>
            </div>
            <div class="border-t border-slate-200 dark:border-white/5 pt-2.5">
              <p class="text-xs text-slate-700 dark:text-gray-300 leading-normal whitespace-pre-line">{{ floatingCards[0].desc }}</p>
            </div>
            <div v-if="floatingCards[0].showSparkline" class="w-full h-10 mt-1">
              <svg class="w-full h-full text-emerald-600" viewBox="0 0 100 30" fill="none" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="sparkline-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="currentColor" stop-opacity="0.25"/>
                    <stop offset="100%" stop-color="currentColor" stop-opacity="0.0"/>
                  </linearGradient>
                </defs>
                <path d="M0,20 Q15,8 30,18 T60,10 T80,22 T100,12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M0,20 Q15,8 30,18 T60,10 T80,22 T100,12 L100,30 L0,30 Z" fill="url(#sparkline-grad)"></path>
                <circle cx="100" cy="12" r="2" fill="currentColor" class="animate-pulse"></circle>
              </svg>
            </div>
          </div>
        </div>

        <!-- Card 2 -->
        <div v-if="floatingCards[1]" class="hidden lg:block absolute top-[50%] right-[14%] lg:right-[16%] z-20 pointer-events-auto" :style="{ transform: parallaxFast }">
          <div class="backdrop-blur-xl bg-white/70 dark:bg-[#0a0f16]/75 p-4 rounded-2xl shadow-2xl border border-white/30 dark:border-white/10 flex items-center gap-3.5 w-[210px] animate-float-medium transition-all duration-300 hover:scale-105 hover:border-teal-500/30" style="animation-delay: -1.5s">
            <div class="w-9 h-9 rounded-full bg-teal-500/10 flex items-center justify-center text-teal-700 dark:text-teal-400 flex-shrink-0">
              <FeatureIcon :name="floatingCards[1].icon || 'chip'" class-name="w-4 h-4" />
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-900 dark:text-white leading-tight">{{ floatingCards[1].title }}</h4>
              <div v-if="floatingCards[1].badgeText" class="flex items-center gap-1.5 mt-0.5">
                <span class="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse inline-block"></span>
                <span class="text-[10px] text-emerald-700 dark:text-emerald-400 font-bold ml-1">{{ floatingCards[1].badgeText }}</span>
              </div>
              <p v-else class="text-[10px] text-slate-600 dark:text-gray-400 leading-tight mt-0.5 whitespace-pre-line">{{ floatingCards[1].desc }}</p>
            </div>
          </div>
        </div>

        <!-- Card 3 -->
        <div v-if="floatingCards[2]" class="hidden lg:block absolute bottom-[18%] right-[6%] lg:right-[8%] z-20 pointer-events-auto" :style="{ transform: parallaxSlow }">
          <div class="backdrop-blur-xl bg-white/70 dark:bg-[#0a0f16]/75 p-4 rounded-2xl shadow-2xl border border-white/30 dark:border-white/10 flex items-center gap-3.5 w-[220px] animate-float-fast transition-all duration-300 hover:scale-105 hover:border-amber-500/30" style="animation-delay: -0.7s">
            <div class="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-700 dark:text-amber-400 flex-shrink-0">
              <FeatureIcon :name="floatingCards[2].icon || 'shield'" class-name="w-5 h-5" />
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-900 dark:text-white leading-tight">{{ floatingCards[2].title }}</h4>
              <div v-if="floatingCards[2].badgeText" class="flex items-center gap-1.5 mt-0.5">
                <span class="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse inline-block"></span>
                <span class="text-[10px] text-emerald-700 dark:text-emerald-400 font-bold ml-1">{{ floatingCards[2].badgeText }}</span>
              </div>
              <p v-else class="text-[10px] text-slate-600 dark:text-gray-400 leading-tight mt-0.5 whitespace-pre-line">{{ floatingCards[2].desc }}</p>
            </div>
          </div>
        </div>
      </div>
      </section>

    <div v-if="homeShowHeroFeaturesBar && heroFeaturesBarItems.length > 0" class="relative z-30 -mt-10 sm:-mt-12 lg:-mt-16 w-[92%] max-w-7xl mx-auto bg-emerald-900/95 dark:bg-emerald-950/95 backdrop-blur-md rounded-2xl md:rounded-3xl border border-emerald-500/20 shadow-2xl p-4 md:p-6 transition-all duration-300">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 divide-y lg:divide-y-0 sm:divide-y-0 lg:divide-x divide-white/10">
        <div v-for="(item, index) in heroFeaturesBarItems" :key="index" class="flex items-center gap-4 px-2 md:px-4 group/item hover:translate-y-[-2px] transition-transform duration-300" :class="{'pt-3 sm:pt-0': index === 1, 'pt-3 lg:pt-0': index >= 2}">
          <div class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-emerald-300 flex-shrink-0 group-hover/item:bg-emerald-500/20 group-hover/item:scale-110 transition-all duration-300">
            <FeatureIcon :name="item.icon || 'water'" class-name="w-5 h-5 text-emerald-300" />
          </div>
          <div>
            <div class="text-xs md:text-sm font-black text-white group-hover/item:text-emerald-200 transition-colors duration-300">{{ item.title }}</div>
            <div class="text-[10px] md:text-xs text-emerald-50 mt-0.5 leading-tight font-medium">{{ item.desc }}</div>
          </div>
        </div>
      </div>
    </div>

    <section v-if="settingsStore.isHolidayActive" class="relative w-full overflow-hidden bg-gradient-to-r from-orange-700 via-amber-600 to-orange-700 border-b border-orange-800/50 shadow-inner">
      <div v-if="settingsStore.holidayImage" class="absolute inset-0 bg-cover bg-center opacity-30 mix-blend-overlay" :style="{ backgroundImage: `url(${settingsStore.holidayImage})` }"></div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10 relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12">
        <div class="flex-1 text-center md:text-left flex flex-col items-center md:items-start">
          <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white font-black tracking-widest text-xs lg:text-sm uppercase mb-4 shadow-sm">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
            {{ settingsStore.holidayName || 'ประกาศพิเศษ' }}
          </div>
          <h2 class="text-xl md:text-2xl lg:text-3xl font-bold text-white leading-relaxed mb-4 text-shadow-md">
            {{ settingsStore.holidayMessage }}
          </h2>
          <div v-if="settingsStore.holidayStartDate && settingsStore.holidayEndDate" class="inline-flex items-center gap-2 bg-black/20 rounded-full px-4 py-1.5 text-sm text-white font-bold tracking-wide border border-black/10">
             <svg class="w-4 h-4 text-orange-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
             {{ new Date(settingsStore.holidayStartDate).toLocaleDateString('th-TH', {day:'numeric', month:'short'}) }}
             {{ '›' }}
             {{ new Date(settingsStore.holidayEndDate).toLocaleDateString('th-TH', {day:'numeric', month:'short', year:'numeric'}) }}
          </div>
        </div>
        <div v-if="settingsStore.holidayImage" class="w-full md:w-1/3 max-w-[350px] shrink-0">
          <img :src="getOptimizedImageUrl(settingsStore.holidayImage, 500)" class="w-full h-auto object-contain rounded-2xl shadow-2xl ring-4 ring-white/20 transform hover:-translate-y-2 transition-transform duration-500 bg-white/5 backdrop-blur-sm" alt="Holiday Campaign" loading="eager">
        </div>

      </div>
    </section>





    <!-- Highlight Categories section - Redesigned Compact Bento -->
    <section v-if="homeShowHighlightCategories" class="py-10 lg:py-14 bg-white dark:bg-[#0a0f16] overflow-hidden relative transition-colors duration-500">
      <!-- Subtle ambient glow -->
      <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/[0.03] dark:bg-emerald-500/[0.05] rounded-full blur-[120px] pointer-events-none"></div>
      <div class="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-500/[0.03] dark:bg-teal-500/[0.04] rounded-full blur-[100px] pointer-events-none"></div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <!-- Compact Header Row with Category Pill Tabs -->
        <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span class="w-5 h-[2px] bg-emerald-600 rounded-full"></span>
              <span class="text-[10px] font-black tracking-[0.2em] uppercase text-emerald-800 dark:text-emerald-400 font-sans">Premium Collection</span>
            </div>
            <h2 class="text-2xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mt-1">
              {{ homeHighlightSettings?.title || 'ค้นพบโซลูชันพื้นที่เก็บของ' }}<span class="text-emerald-700 dark:text-emerald-400 font-extrabold"> เหนือระดับ</span>
            </h2>
          </div>
          
          <!-- Horizontal Category Pill Tabs -->
          <div class="flex items-center gap-1.5 overflow-x-auto hide-scrollbar pb-1 select-none">
            <button v-for="(cat, idx) in highlightCategoriesList.slice(0, 4)" :key="cat.id" @click="setHighlightCat(idx)" :aria-label="`ดูหมวดหมู่ที่ ${idx + 1}`"
              class="relative flex-shrink-0 px-4 py-2 rounded-full text-[11px] font-bold transition-all duration-300 cursor-pointer border whitespace-nowrap"
              :class="highlightCatIndex === idx 
                ? 'bg-emerald-700 text-white border-emerald-700 shadow-lg shadow-emerald-500/20' 
                : 'bg-slate-100 dark:bg-gray-800/80 text-slate-900 dark:text-gray-100 border-slate-200 dark:border-gray-700/50 hover:border-emerald-500 hover:text-emerald-900 dark:hover:text-emerald-400'">
              {{ cat.name }}
              <!-- Progress indicator on active tab -->
              <span v-if="highlightCatIndex === idx" class="absolute bottom-0.5 left-3 right-3 h-[2px] bg-white/20 rounded-full overflow-hidden">
                <span class="absolute inset-y-0 left-0 bg-white/60 rounded-full transition-none" :style="{ width: highlightProgress + '%' }"></span>
              </span>
            </button>
          </div>
        </div>

        <!-- Main Content Card -->
        <transition name="fade-slide-up" mode="out-in">
          <div :key="highlightCatIndex" class="relative rounded-[1.5rem] overflow-hidden bg-[#f8faf9] dark:bg-[#111827] border border-gray-100 dark:border-gray-800/80 shadow-xl shadow-gray-200/50 dark:shadow-black/30">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-0">
              
              <!-- Image Side -->
              <div class="relative h-[260px] sm:h-[300px] lg:h-auto lg:min-h-[440px] overflow-hidden">
                <img :src="getOptimizedImageUrl(highlightCategoriesList[highlightCatIndex]?.image_url, 1200) || '/images/placeholder.png'" 
                  :alt="highlightCategoriesList[highlightCatIndex]?.name || 'หมวดหมู่สินค้า'" 
                  class="w-full h-full object-cover transition-all duration-700 scale-105 hover:scale-100" />

                <!-- Gradient overlays -->
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#f8faf9]/90 dark:to-[#111827]/90 hidden lg:block pointer-events-none"></div>
                <div class="absolute inset-0 bg-gradient-to-t from-[#f8faf9] dark:from-[#111827] via-transparent to-transparent lg:hidden pointer-events-none"></div>
                


                <!-- Bottom Glassmorphic Spec Bar (Mobile & Desktop) -->
                <div v-if="highlightCategoriesList[highlightCatIndex]?.tags?.length > 0" class="absolute bottom-4 left-4 right-4 z-10 hidden sm:block">
                  <div class="backdrop-blur-lg bg-slate-950/70 px-4 py-3 rounded-2xl border border-white/10 shadow-xl flex items-center justify-around gap-3">
                    <template v-for="(tag, tagIdx) in highlightCategoriesList[highlightCatIndex].tags" :key="tagIdx">
                      <div v-if="tagIdx > 0" class="h-5 w-[1px] bg-white/10"></div>
                      <div class="flex items-center gap-2 text-center">
                        <div class="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                          <svg v-if="tagIdx === 0" class="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M20 7.242v7.517a2.138 2.138 0 01-1.07 1.851l-6.28 3.634a2.136 2.136 0 01-2.138 0l-6.28-3.634A2.138 2.138 0 013 14.759V7.242A2.138 2.138 0 014.07 5.39l6.28-3.634a2.138 2.138 0 012.138 0l6.28 3.634A2.138 2.138 0 0120 7.242z" /><path stroke-linecap="round" stroke-linejoin="round" d="M12 22V12m0 0l8-4.62M12 12L4 7.38" /></svg>
                          <svg v-else-if="tagIdx === 1" class="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                          <svg v-else class="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <span class="text-[10px] font-bold text-white leading-tight">{{ tag }}</span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <!-- Info Side -->
              <div class="p-5 sm:p-7 lg:p-8 flex flex-col justify-between">
                <!-- Category Info -->
                <div>
                  <div class="flex items-center gap-2.5 mb-2">
                    <span class="inline-flex items-center justify-center bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-400 text-[10px] font-black px-2.5 py-0.5 rounded-full font-mono border border-emerald-200/50 dark:border-emerald-800/30">
                      No. {{ highlightCatIndex + 1 }}
                    </span>
                  </div>
                  <h3 class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white leading-tight mb-2">
                    {{ highlightCategoriesList[highlightCatIndex]?.name }}
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-5 line-clamp-2 max-w-lg">
                    {{ highlightCategoriesList[highlightCatIndex]?.customDescription || (highlightCategoriesList[highlightCatIndex]?.name?.includes('โลหะ') ? 'เสริมความมั่นคงและความปลอดภัยสูงสุดด้วยโครงสร้างเหล็กคุณภาพ แข็งแรงทนทาน ปกป้องทรัพย์สินของคุณได้อย่างไร้กังวล' : 'ดีไซน์ทันสมัย สไตล์มินิมอล แข็งแรงทนทาน ตอบโจทย์ทุกการใช้สอยกลางแจ้ง') }}
                  </p>

                  <!-- Compact Feature Chips -->
                  <div class="flex flex-wrap gap-2 mb-6">
                    <div v-for="(feature, fIdx) in activeCategoryFeatures" :key="fIdx" 
                      class="group/feat inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 shadow-sm hover:shadow-md hover:border-emerald-200 dark:hover:border-emerald-800/50 transition-all duration-300">
                      <!-- Compact icon -->
                      <div class="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 flex items-center justify-center flex-shrink-0 group-hover/feat:bg-emerald-100 dark:group-hover/feat:bg-emerald-900/30 transition-colors">
                        <FeatureIcon :name="feature.icon || 'check'" class-name="w-3.5 h-3.5" />
                      </div>
                      <span class="text-[11px] font-bold text-gray-800 dark:text-gray-300 group-hover/feat:text-emerald-800 dark:group-hover/feat:text-emerald-400 transition-colors">{{ feature.title }}</span>
                    </div>
                  </div>
                </div>

                <!-- Products Row -->
                <div>
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-1.5">
                      <span class="text-emerald-600 font-black text-sm">/</span>
                      <h4 class="text-[10px] font-black text-gray-600 dark:text-gray-400 uppercase tracking-widest font-sans">สินค้าแนะนำ</h4>
                    </div>
                    <div class="flex gap-1">
                      <button @click="scrollProductsLeft" aria-label="เลื่อนซ้าย" class="w-7 h-7 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all cursor-pointer">
                        <span class="sr-only">เลื่อนซ้าย</span>
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"></path></svg>
                      </button>
                      <button @click="scrollProductsRight" aria-label="เลื่อนขวา" class="w-7 h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center active:scale-95 transition-all cursor-pointer shadow-sm">
                        <span class="sr-only">เลื่อนขวา</span>
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path></svg>
                      </button>
                    </div>
                  </div>
                  
                  <div ref="productSliderRef" class="flex gap-3 overflow-x-auto hide-scrollbar scroll-smooth pb-2 snap-x snap-mandatory" style="scrollbar-width: none; -ms-overflow-style: none;">
                    <router-link v-for="product in highlightCategoryProducts[highlightCatIndex]" :key="product.id" :to="`/products/${product.slug || product.id}`" class="group/mini flex-shrink-0 w-[120px] sm:w-[135px] bg-white dark:bg-[#1a2332] border border-gray-100 dark:border-white/5 rounded-xl p-2.5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden">
                      <div class="relative aspect-square rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800 mb-2 border border-gray-50 dark:border-gray-700/50">
                        <img :src="getOptimizedImageUrl(product.image_url, 300) || '/images/placeholder.png'" :alt="product.name || 'สินค้ารุ่นแนะนำ'" class="w-full h-full object-cover group-hover/mini:scale-105 transition-transform duration-500" />
                        <div v-if="product.is_out_of_stock" class="absolute inset-0 bg-white/50 dark:bg-black/50 flex items-center justify-center z-[2]">
                          <span class="text-[10px] font-black text-gray-500/50 -rotate-12 select-none pointer-events-none tracking-widest">หมดแล้ว</span>
                        </div>
                      </div>
                      <h4 class="font-bold text-gray-900 dark:text-white text-[11px] line-clamp-1 leading-snug mb-1 group-hover/mini:text-emerald-700 transition-colors">{{ product.name }}</h4>
                      <p class="text-red-700 dark:text-red-400 font-extrabold text-xs">฿{{ Number(product.price).toLocaleString() }}</p>
                    </router-link>
                  </div>
                </div>

                <!-- Compact CTA -->
                <router-link :to="`/products?category=${encodeURIComponent(highlightCategoriesList[highlightCatIndex]?.name)}`" class="group flex items-center justify-between mt-5 p-3 sm:p-3.5 bg-gradient-to-r from-[#0e291b] to-[#163f2b] dark:from-[#0d2218] dark:to-[#122e20] text-white rounded-xl transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5 border border-emerald-800/30">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center text-emerald-300 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
                    </div>
                    <div class="text-left">
                      <h4 class="font-extrabold text-xs sm:text-sm text-white tracking-wide">ดูสินค้าทั้งหมดในหมวดนี้</h4>
                      <p class="text-[10px] text-emerald-200/70 font-medium">เลือกดูสินค้าทั้งหมดของเราได้ที่นี่</p>
                    </div>
                  </div>
                  <div class="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-white border border-white/10 group-hover:bg-emerald-500 group-hover:border-emerald-400 group-hover:translate-x-0.5 transition-all flex-shrink-0">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path></svg>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════ -->
    <!-- Category Showcase - Admin Configured (No main header) -->
    <!-- ═══════════════════════════════════════════════════════════ -->
    <section v-if="showcaseCategoriesData.length > 0" class="relative py-12 md:py-20 overflow-hidden bg-white dark:bg-[#0b1120] border-t border-gray-100 dark:border-gray-800/50 transition-colors duration-300">
      <!-- Decorative Background Glows -->
      <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/[0.03] dark:bg-emerald-500/[0.06] rounded-full blur-[120px] pointer-events-none"></div>
      <div class="absolute bottom-0 left-0 w-[400px] h-[400px] bg-teal-500/[0.03] dark:bg-teal-500/[0.06] rounded-full blur-[100px] pointer-events-none"></div>
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-br from-emerald-500/[0.02] to-cyan-500/[0.02] dark:from-emerald-500/[0.04] dark:to-cyan-500/[0.04] rounded-full blur-[150px] pointer-events-none"></div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <!-- Categories Showcase List (Vertically Arranged) -->
        <div class="space-y-16 md:space-y-24">
          <div
            v-for="(showcase, idx) in showcaseCategoriesData"
            :key="'showcase-cat-' + idx"
            class="relative border-b border-gray-100 dark:border-gray-800/80 pb-16 md:pb-20 last:border-b-0 last:pb-0"
          >

            <!-- Category Title & View All Link -->
            <div class="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 md:mb-8 pb-4 border-b border-gray-150/40 dark:border-gray-800/40">
              <div>
                <div class="flex items-center gap-2 mb-2">
                  <span class="w-3 h-3 rounded-full bg-gradient-to-tr from-emerald-600 to-teal-600"></span>
                  <span class="text-[10px] font-black text-emerald-800 dark:text-emerald-400 uppercase tracking-widest font-mono">
                    หมวดหมู่แนะนำ
                  </span>
                </div>
                <h2 class="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white leading-tight">
                  {{ showcase.category?.name || 'หมวดหมู่สินค้า' }}
                </h2>
              </div>
              
              <router-link
                :to="'/products?category=' + encodeURIComponent(showcase.category?.name || '')"
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-200/60 dark:border-gray-700/80 bg-white/80 dark:bg-white/[0.04] backdrop-blur-md text-gray-900 dark:text-gray-200 text-xs font-bold hover:border-emerald-500 dark:hover:border-emerald-600 hover:text-emerald-800 dark:hover:text-emerald-400 hover:shadow-md hover:shadow-emerald-500/5 transition-all duration-300 group"
              >
                <span>ดูสินค้าทั้งหมดใน {{ showcase.category?.name }}</span>
                <svg class="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </router-link>
            </div>

            <!-- Features list (if enabled and present) -->
            <div v-if="showcase.category?.features?.enabled && showcase.category?.features?.items?.length > 0"
                 class="flex flex-wrap gap-2 mb-8">
              <span v-for="(feature, fIdx) in showcase.category.features.items.slice(0, 6)" :key="'feat-' + idx + '-' + fIdx"
                    class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-50/50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-400 border border-emerald-200/30 dark:border-emerald-500/15">
                <svg class="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                {{ typeof feature === 'string' ? feature : feature.text || feature.name || feature }}
              </span>
            </div>

            <!-- Products Grid for this category -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 xl:gap-8">
              <ProductCard
                v-for="(product, pIdx) in showcase?.products || []"
                :key="'showcase-prod-' + product.id"
                :product="{
                  id: product.id,
                  slug: product.slug,
                  name: product.title,
                  image_url: product.image,
                  sku: product.sku,
                  price: product.price,
                  original_price: product.originalPrice,
                  is_out_of_stock: product.is_out_of_stock,
                  category: product.subtitle,
                  card_features: product.card_features,
                  compare_enabled: product.compare_enabled
                }"
              />

            </div>
          </div>
        </div>
      </div>
    </section>



    <!-- Recent Projects Showcase -->
    <section v-if="recentProjects.length >= 5" class="py-20 md:py-28 bg-[#f8f9fa] dark:bg-[#0f172a] transition-colors duration-300">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-16 gap-6">
          <div>
            <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-400 border border-emerald-500/10 mb-4 select-none">
              <svg class="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span class="uppercase tracking-widest">{{ sectionTitles.projectsTitle }}</span>
            </div>
            <h2 class="text-3xl md:text-4xl font-black tracking-tight text-gray-900 dark:text-white mb-2">
              {{ sectionTitles.projectsHeading.endsWith('ล่าสุด') ? sectionTitles.projectsHeading.slice(0, -5) : sectionTitles.projectsHeading }}<span class="text-emerald-700 dark:text-emerald-400">{{ sectionTitles.projectsHeading.endsWith('ล่าสุด') ? 'ล่าสุด' : '' }}</span>
            </h2>
            <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400 font-light">รวมผลงานติดตั้งจริงจากทั่วประเทศ คุณภาพที่พิสูจน์ได้จากความไว้วางใจของลูกค้า</p>
          </div>
          <router-link to="/projects" class="group inline-flex text-xs font-bold tracking-wider text-emerald-900 dark:text-emerald-400 uppercase hover:text-emerald-950 dark:hover:text-emerald-300 items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-emerald-700/30 dark:border-emerald-500/30 hover:border-emerald-700 dark:hover:border-emerald-450 transition-all hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 whitespace-nowrap bg-white dark:bg-slate-900 shadow-sm">
            ดูผลงานทั้งหมด <svg class="w-3.5 h-3.5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
          </router-link>
        </div>

        <!-- Bento / Mosaic Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" style="grid-template-rows: repeat(2, 320px);">
          <!-- Loop over recentProjects -->
          <template v-for="(project, idx) in recentProjects" :key="project.id">
            <!-- Featured Card (Left Column - Spans 2 Rows) -->
            <router-link 
              v-if="idx === 0"
              :to="'/projects/' + (project.slug || project.id)" 
              class="group relative block rounded-3xl overflow-hidden shadow-md hover:shadow-2xl hover:shadow-emerald-500/10 transition-all duration-500 md:row-span-2 hover:-translate-y-1"
            >
              <!-- Image -->
              <img :src="getOptimizedImageUrl(project.cover_image, 700)" :alt="project.title" class="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-[1200ms] ease-out">
              
              <!-- Gradient Overlay -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/5 group-hover:from-black/90 group-hover:via-black/45 transition-all duration-500 z-10"></div>
              
              <!-- Badge "ผลงานแนะนำ" -->
              <div class="absolute top-4 left-4 z-20">
                <span class="inline-flex items-center gap-1.5 bg-[#026943] text-white text-[10px] font-black px-3.5 py-1.5 rounded-full border border-emerald-500/20 shadow-md">
                  <svg class="w-3 h-3 text-amber-400 fill-current" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  ผลงานแนะนำ
                </span>
              </div>

              <!-- Floating Box Icon Button -->
              <div class="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-white dark:bg-slate-900 border border-gray-150/20 dark:border-white/10 text-emerald-700 dark:text-emerald-400 shadow-md flex items-center justify-center">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>

              <!-- Content at bottom -->
              <div class="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-20">
                <h3 class="font-bold text-white leading-snug mb-3 line-clamp-2 drop-shadow-lg text-xl md:text-2xl font-heading">
                  {{ project.title }}
                </h3>
                <div class="flex flex-wrap items-center gap-4 text-white/80 mb-5">
                  <p class="flex items-center gap-1.5 text-xs md:text-sm font-medium">
                    <svg class="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                    {{ project.location || 'ประเทศไทย' }}
                  </p>
                  <p v-if="project.service_date" class="flex items-center gap-1.5 text-xs md:text-sm font-medium">
                    <svg class="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                    {{ new Date(project.service_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short' }) }}
                  </p>
                </div>
                <!-- Premium Details Button/Badge -->
                <div class="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white text-xs font-bold px-4 py-2.5 rounded-full border border-white/20 transition-all duration-300">
                  <span>ดูรายละเอียด</span>
                  <div class="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                    <svg class="w-3.5 h-3.5 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </router-link>

            <!-- Small Card Layout (Middle and Right Columns) -->
            <router-link 
              v-else-if="idx === 1 || idx === 2 || idx === 3"
              :to="'/projects/' + (project.slug || project.id)" 
              :aria-label="project.title"
              :class="[
                'group relative flex flex-col rounded-3xl overflow-hidden bg-white dark:bg-slate-900 border border-gray-150/40 dark:border-white/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500',
                idx >= 2 ? 'hidden md:flex' : 'flex'
              ]"
            >
              <!-- Card Image Area -->
              <div class="relative w-full h-[175px] overflow-hidden bg-gray-150 dark:bg-gray-800">
                <img :src="getOptimizedImageUrl(project.cover_image, 500)" :alt="project.title" class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500">
                
                <!-- Overlay badge for 'ผลงานติดตั้ง' -->
                <div class="absolute bottom-2.5 left-3 z-15">
                  <span class="inline-flex items-center bg-emerald-100/90 dark:bg-emerald-950/85 text-emerald-900 dark:text-emerald-300 text-[10px] font-black px-2.5 py-1 rounded-full shadow-sm select-none">
                    ผลงานติดตั้ง
                  </span>
                </div>

                <!-- Floating Heart Icon Button -->
                <button @click.prevent="" aria-label="ถูกใจผลงาน" class="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/90 dark:bg-slate-900/90 hover:bg-white dark:hover:bg-slate-900 text-gray-600 hover:text-red-500 shadow-sm flex items-center justify-center transition-colors z-20">
                  <span class="sr-only">ถูกใจผลงาน</span>
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>

            <!-- Card Content Area -->
            <div class="p-4 flex-1 flex flex-col justify-between relative bg-white dark:bg-slate-900">
              <div>
                <h3 class="font-bold text-gray-900 dark:text-white text-sm md:text-base leading-snug line-clamp-2 mb-3 group-hover:text-emerald-700 dark:group-hover:text-emerald-450 transition-colors">
                  {{ project.title }}
                </h3>
                <div class="flex flex-col gap-1.5 text-gray-600 dark:text-gray-400">
                  <p class="flex items-center gap-1.5 text-xs font-medium">
                    <svg class="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                    {{ project.location || 'ประเทศไทย' }}
                  </p>
                  <p v-if="project.service_date" class="flex items-center gap-1.5 text-xs font-medium">
                    <svg class="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                    {{ new Date(project.service_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short' }) }}
                  </p>
                </div>
              </div>

              <!-- Floating white circle arrow button at bottom right -->
              <div class="absolute bottom-4 right-4 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border border-gray-150/40 dark:border-white/5 flex items-center justify-center text-emerald-700 dark:text-emerald-450 shadow-sm group-hover:bg-emerald-50 dark:group-hover:bg-emerald-950/30 transition-all duration-300">
                <svg class="w-3.5 h-3.5 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </div>
          </router-link>
        </template>

        <!-- Service CTA Card (fills bottom right cell) -->
        <router-link 
          to="/quotation" 
          class="hidden lg:flex group relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#025a38] via-[#01492d] to-[#003822] flex-col justify-between p-6 transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-500/20"
        >
          <!-- Curvy lines pattern -->
          <div class="absolute inset-y-0 right-0 w-1/2 opacity-20 pointer-events-none z-0">
            <svg class="w-full h-full" viewBox="0 0 150 200" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 200 C 50 150, 100 120, 150 130" stroke="#4ade80" stroke-width="1.5" />
              <path d="M0 200 C 40 130, 90 90, 150 105" stroke="#4ade80" stroke-width="1.5" />
              <path d="M-20 200 C 30 110, 80 60, 150 80" stroke="#4ade80" stroke-width="1.5" />
              <path d="M-40 200 C 20 90, 70 30, 150 55" stroke="#4ade80" stroke-width="1.5" />
              <path d="M-60 200 C 10 70, 60 0, 150 30" stroke="#4ade80" stroke-width="1.5" />
            </svg>
          </div>
          
          <div class="relative z-10 flex-1 flex flex-col justify-between">
            <!-- Headset support icon in circle container -->
            <div class="w-9 h-9 rounded-full border border-white/20 bg-white/10 flex items-center justify-center mb-6">
              <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            
            <div>
              <h4 class="text-lg font-bold text-white mb-2 leading-snug">บริการติดตั้งทั่วประเทศ</h4>
              <p class="text-emerald-100/70 text-xs font-light leading-relaxed mb-6">ทีมช่างมืออาชีพพร้อมบริการติดตั้งถึงหน้าบ้าน รวดเร็ว ปลอดภัย รับประกันคุณภาพ</p>
            </div>
              
            <div class="flex">
              <span class="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#01492d] hover:bg-gray-150 transition-colors text-xs font-bold shadow-sm">
                ขอใบเสนอราคา
                <svg class="w-3.5 h-3.5 text-[#01492d]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </div>
          </div>
        </router-link>
      </div>

      </div>
    </section>



    <section 
      v-if="recentArticles.length > 0" 
      class="py-24 md:py-32 bg-[#f8f9fa] dark:bg-[#0f172a] border-t border-gray-100 dark:border-white/5 relative overflow-hidden"
      @mouseenter="stopArticleAutoplay"
      @mouseleave="startArticleAutoplay"
    >
      <!-- Decor -->
      <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div class="absolute top-40 right-[-10%] w-96 h-96 bg-emerald-500/5 blur-[100px] rounded-full"></div>
        <div class="absolute bottom-10 left-[-5%] w-80 h-80 bg-teal-500/5 blur-[100px] rounded-full"></div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-16 gap-6">

          <div>
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/60 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-300 text-xs font-bold tracking-widest uppercase mb-4 border border-emerald-300/60 dark:border-emerald-800/50">
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span>
              {{ sectionTitles.articlesTitle }}
            </div>
            <h2 class="text-3xl md:text-5xl font-black tracking-tight text-gray-900 dark:text-white">{{ sectionTitles.articlesHeading }}</h2>
            <p class="text-xs md:text-sm text-gray-600 dark:text-gray-400 font-light max-w-2xl mt-4 leading-relaxed">
              ติดตามบทความและข่าวสารล่าสุดเกี่ยวกับโรงเรือนปลูกพืชอัจฉริยะ เทรนด์การเกษตร เทคโนโลยี IoT และไอเดียการทำสวนที่เราคัดสรรมาเพื่อคุณ
            </p>
          </div>
          <router-link to="/articles" class="group inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-sm font-bold text-gray-900 dark:text-white rounded-full shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-700 hover:border-emerald-500 dark:hover:border-emerald-500 transition-all duration-300 hover:-translate-y-0.5">
            อ่านบทความทั้งหมด
            <div class="w-6 h-6 rounded-full bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 group-hover:bg-[#026943] group-hover:text-white flex items-center justify-center transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"></path></svg>
            </div>
          </router-link>
        </div>

        <div class="overflow-hidden mx-[-1rem]">
          <div 
            class="flex w-full transition-transform duration-500 ease-in-out"
            :style="{ transform: 'translateX(calc(-1 * ' + currentPageIndex + ' * 100%))' }"
          >
            <div 
              v-for="(article, idx) in recentArticles" 
              :key="article.id"
              class="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4 relative group"
            >

              <!-- Recommended Badge overlay for middle card -->
              <div v-if="idx === 1" class="absolute -top-3.5 left-1/2 transform -translate-x-1/2 z-30">
                <span class="inline-flex items-center bg-[#026943] text-white text-[10px] font-black px-4 py-1.5 rounded-full border border-emerald-500/20 shadow-md whitespace-nowrap">
                  แนะนำสำหรับคุณ
                </span>
              </div>

              <router-link 
                :to="'/articles/' + article.slug" 
                class="flex flex-col h-full bg-white dark:bg-[#1a2333] rounded-[2rem] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgb(16,185,129,0.12)] transition-all duration-500 border hover:-translate-y-2"
                :class="idx === 1 
                  ? 'border-[#026943] border-2 ring-4 ring-[#026943]/5 shadow-[#026943]/5' 
                  : 'border-gray-100 dark:border-white/5 hover:border-emerald-250 dark:hover:border-emerald-500/30'"
              >
                <div class="aspect-[16/10] overflow-hidden relative">
                  <img :src="getOptimizedImageUrl(article.cover_image, 600)" :alt="article.title" class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-[1.5s] ease-out">
                  <div class="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0 opacity-40 group-hover:opacity-70 transition-opacity duration-500"></div>
                  
                  <!-- Floating Date Badge -->
                  <div class="absolute top-5 left-5 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md px-3.5 py-1.5 rounded-full shadow-sm border border-white/20">
                    <p class="text-[10px] font-bold tracking-wider text-emerald-600 dark:text-emerald-400 uppercase flex items-center gap-1.5">
                      <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {{ formatArticleDate(article.published_at || article.created_at) }}
                    </p>
                  </div>

                  <!-- Bookmark Button for card 2 and 3 -->
                  <button 
                    v-if="idx === 1 || idx === 2"
                    @click.prevent="toggleBookmark(article.id)" 
                    aria-label="บุ๊กมาร์กบทความ"
                    class="absolute top-5 right-5 z-20 w-9 h-9 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center shadow-md hover:scale-105 active:scale-95 transition-all border border-white/10"
                  >
                    <span class="sr-only">บุ๊กมาร์กบทความ</span>
                    <svg 
                      class="w-4 h-4 text-[#026943] dark:text-emerald-400" 
                      :class="{ 'fill-current text-[#026943] dark:text-emerald-400': isBookmarked(article.id) }" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor" 
                      stroke-width="2"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                  </button>

                  <!-- Tags overlay on Image -->
                  <div class="absolute bottom-4 left-5 flex gap-2 z-20">
                    <span 
                      v-for="tag in (article.tags ? article.tags.split(',').slice(0, 2) : [])" 
                      :key="tag"
                      class="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold bg-black/45 backdrop-blur-md text-white rounded-full border border-white/10"
                    >
                      <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M7 7h.01M6 20l6.5-6.5A2.5 2.5 0 0020 10V4h-6a2.5 2.5 0 00-3.5 2.5L4 13.5V20h2z" />
                      </svg>
                      {{ tag }}
                    </span>
                  </div>
                </div>
                
                <div class="p-6 md:p-8 flex-grow flex flex-col justify-between relative">
                  <div>
                    <!-- Category Badge -->
                    <div class="mb-3">
                      <span class="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-[#026943] dark:text-emerald-450">
                        {{ article.category || (idx === 0 ? 'บทความแนะนำ' : idx === 1 ? 'บทความยอดนิยม' : 'ไอเดีย & แรงบันดาลใจ') }}
                      </span>
                    </div>

                    <!-- Title -->
                    <h3 
                      class="font-bold text-xl md:text-2xl mb-3 line-clamp-2 leading-tight transition-colors"
                      :class="idx === 1 
                        ? 'text-[#026943] dark:text-emerald-400' 
                        : 'text-gray-900 dark:text-white group-hover:text-[#026943] dark:group-hover:text-emerald-400'"
                    >
                      {{ article.title }}
                    </h3>

                    <!-- Excerpt -->
                    <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 leading-relaxed mb-6">
                      {{ article.excerpt || 'อ่านรายละเอียดเชิงลึกและเทคนิคดีๆ เพิ่มเติมได้ภายในบทความนี้' }}
                    </p>
                  </div>

                  <!-- Card Footer (Read Link) -->
                  <div class="mt-auto pt-5 border-t border-gray-100 dark:border-white/5 flex items-center justify-between text-sm font-bold transition-colors">
                    <span :class="idx === 1 ? 'text-[#026943] dark:text-emerald-400' : 'text-gray-900 dark:text-white group-hover:text-[#026943] dark:group-hover:text-emerald-400'">
                      อ่านบทความ
                    </span>
                    
                    <div 
                      class="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
                      :class="idx === 1 
                        ? 'bg-[#026943] text-white shadow-md shadow-[#026943]/20' 
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white group-hover:bg-[#026943]/10 dark:group-hover:bg-[#026943]/20 group-hover:text-[#026943] dark:group-hover:text-emerald-400'"
                    >
                      <svg class="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </router-link>
            </div>
          </div>
        </div>

        <!-- Slider Navigation Controls -->
        <div class="flex justify-center items-center gap-6 mt-16">

          <button 
            @click="prevArticleSlide"
            aria-label="บทความก่อนหน้า"
            class="w-10 h-10 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center text-gray-650 dark:text-gray-350 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all shadow-sm"
          >
            <span class="sr-only">บทความก่อนหน้า</span>
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          
          <div class="flex items-center gap-2">
            <span 
              v-for="dotIdx in totalPages" 
              :key="dotIdx"
              @click="goToArticleSlide(dotIdx - 1)"
              class="rounded-full transition-all duration-300 cursor-pointer"
              :class="currentPageIndex === dotIdx - 1 
                ? 'w-2.5 h-2.5 bg-[#026943]' 
                : 'w-2 h-2 bg-gray-300 dark:bg-gray-700 hover:bg-[#026943]/50'"
            ></span>
          </div>
          
          <button 
            @click="nextArticleSlide"
            aria-label="บทความถัดไป"
            class="w-10 h-10 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-center text-gray-650 dark:text-gray-350 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all shadow-sm"
          >
            <span class="sr-only">บทความถัดไป</span>
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>
    </section>



    <!-- Affiliated Companies Section -->
    <section v-if="homeShowAffiliates && affiliatedCompanies.length > 0" class="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 dark:from-[#0a0f16] dark:to-[#0f172a] transition-colors duration-300 relative overflow-hidden">
      <!-- Wavy Background Decor (Right) -->
      <div class="absolute top-1/4 right-0 w-[500px] h-[600px] text-emerald-500/10 pointer-events-none hidden lg:block select-none opacity-60">
        <svg viewBox="0 0 500 600" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full transform translate-x-12">
          <path d="M150 0 C 300 200, 100 400, 500 600" stroke="currentColor" stroke-width="2" />
          <path d="M180 0 C 330 200, 130 400, 500 570" stroke="currentColor" stroke-width="1.5" />
          <path d="M210 0 C 360 200, 160 400, 500 540" stroke="currentColor" stroke-width="1" stroke-dasharray="4 4" />
          <path d="M240 0 C 390 200, 190 400, 500 510" stroke="currentColor" stroke-width="0.5" />
        </svg>
      </div>

      <!-- Wavy Background Decor (Left) -->
      <div class="absolute bottom-0 left-0 w-[500px] h-[500px] text-emerald-500/10 pointer-events-none hidden lg:block select-none opacity-60">
        <svg viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-full h-full transform -translate-x-12 translate-y-12">
          <path d="M0 350 C 200 300, 300 100, 500 0" stroke="currentColor" stroke-width="2" />
          <path d="M0 380 C 200 330, 300 130, 470 0" stroke="currentColor" stroke-width="1.5" />
          <path d="M0 410 C 200 360, 300 160, 440 0" stroke="currentColor" stroke-width="1" stroke-dasharray="4 4" />
          <path d="M0 440 C 200 390, 300 190, 410 0" stroke="currentColor" stroke-width="0.5" />
        </svg>
      </div>

      <div class="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center mb-10 md:mb-14">

          <div class="flex items-center justify-center gap-3 mb-3">
            <span class="w-8 h-[1px] bg-emerald-600/30"></span>
            <span class="text-xs font-bold tracking-[0.2em] text-emerald-600 dark:text-emerald-400 uppercase">{{ sectionTitles.affiliatesTitle }}</span>
            <span class="w-8 h-[1px] bg-emerald-600/30"></span>
          </div>
          <h2 class="text-3xl md:text-4.5xl font-black tracking-tight text-gray-900 dark:text-white mb-4">{{ sectionTitles.affiliatesHeading }}</h2>
          <p class="text-gray-500 dark:text-gray-400 text-sm md:text-base max-w-2xl mx-auto font-light leading-relaxed">
            บริษัทในเครือที่ร่วมขับเคลื่อนธุรกิจด้วยนวัตกรรม คุณภาพ และความเชี่ยวชาญ เพื่อสร้างคุณค่าและเติบโตไปด้วยกันอย่างมั่นคง
          </p>
        </div>

        <!-- LAYOUT A: Single Affiliate (Split-card layout to save vertical space) -->
        <div v-if="affiliatedCompanies.length === 1" class="max-w-5xl mx-auto">

          <a 
            :href="affiliatedCompanies[0].url" 
            target="_blank" 
            rel="noopener noreferrer"
            class="group flex flex-col md:flex-row bg-white dark:bg-[#1a2333] rounded-[2rem] overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.04)] dark:shadow-[0_15px_40px_rgba(0,0,0,0.2)] hover:shadow-[0_20px_50px_rgba(16,185,129,0.1)] transition-all duration-500 hover:-translate-y-1.5 border border-gray-150/60 dark:border-white/5 relative"
          >
            <!-- Left: Banner Image -->
            <div class="w-full md:w-3/5 relative overflow-hidden aspect-[4/3] md:aspect-auto min-h-[200px] md:min-h-[300px]">
              <img :src="getOptimizedImageUrl(affiliatedCompanies[0].banner, 800)" :alt="affiliatedCompanies[0].name" class="w-full h-full object-cover transform group-hover:scale-[1.04] transition-transform duration-700">
              <div class="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/20 via-transparent to-transparent opacity-60"></div>
            </div>
            <!-- Right: Content & Link -->
            <div class="w-full md:w-2/5 p-6 md:p-8 pt-10 md:pt-8 md:pl-10 flex flex-col justify-between bg-white dark:bg-[#161d2a] relative">
              <!-- Overlapping circular icon badge -->
              <div class="absolute -top-7 left-6 md:top-6 md:-left-7 w-14 h-14 rounded-full bg-white dark:bg-slate-900 border border-gray-150/60 dark:border-white/10 shadow-md flex items-center justify-center z-20">
                <div 
                  class="w-10 h-10 rounded-full flex items-center justify-center"
                  :class="getAffiliateIconName(affiliatedCompanies[0]) === 'chip' ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-emerald-50 dark:bg-emerald-950/30'"
                >
                  <svg v-if="getAffiliateIconName(affiliatedCompanies[0]) === 'lightning'" class="w-5 h-5 text-emerald-600 dark:text-emerald-450" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <svg v-else-if="getAffiliateIconName(affiliatedCompanies[0]) === 'chip'" class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 5h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2zM9 9h6v6H9V9z" />
                  </svg>
                  <svg v-else-if="getAffiliateIconName(affiliatedCompanies[0]) === 'home'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <svg v-else class="w-5 h-5 text-emerald-600 dark:text-emerald-450" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
              </div>

              <div class="space-y-3">
                <h3 class="text-xl md:text-2xl font-black text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors leading-tight">{{ affiliatedCompanies[0].name }}</h3>
                <p v-if="affiliatedCompanies[0].description" class="text-gray-700 dark:text-gray-300 text-xs md:text-sm font-normal leading-relaxed line-clamp-3 md:line-clamp-4">{{ affiliatedCompanies[0].description }}</p>
              </div>
              <div class="mt-8 pt-4 border-t border-gray-100 dark:border-white/5 flex items-center justify-between text-xs md:text-sm font-bold text-emerald-800 dark:text-emerald-300">
                <span class="group-hover:text-emerald-900 dark:group-hover:text-emerald-200 transition-colors">เยี่ยมชมเว็บไซต์</span>
                <div class="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800 group-hover:bg-[#026943] text-gray-700 dark:text-gray-300 group-hover:text-white flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-750 transition-all duration-300">
                  <svg class="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path></svg>
                </div>
              </div>
            </div>
          </a>
        </div>

        <!-- LAYOUT B: Multiple Affiliates (Grid layout) -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-8">
          <a 
            v-for="(company, idx) in affiliatedCompanies" 
            :key="idx"
            :href="company.url" 
            target="_blank" 
            rel="noopener noreferrer"
            class="group flex flex-col bg-white dark:bg-[#1a2333] rounded-[2rem] overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.04)] dark:shadow-[0_15px_40px_rgba(0,0,0,0.2)] hover:shadow-[0_20px_50px_rgba(16,185,129,0.1)] transition-all duration-500 hover:-translate-y-1.5 border border-gray-100 dark:border-white/5 h-full relative"
          >

            <!-- Card Image -->
            <div class="relative overflow-hidden aspect-[4/3] w-full bg-gray-100 dark:bg-gray-800">
              <img :src="getOptimizedImageUrl(company.banner, 600)" :alt="company.name" class="w-full h-full object-cover transform group-hover:scale-[1.04] transition-transform duration-700">
            </div>
            <!-- Card Details -->
            <div class="p-6 pt-10 flex-grow flex flex-col justify-between bg-white dark:bg-[#161d2a] relative">
              <!-- Overlapping circular icon badge -->
              <div class="absolute -top-7 left-6 w-14 h-14 rounded-full bg-white dark:bg-slate-900 border border-gray-150/60 dark:border-white/10 shadow-md flex items-center justify-center z-20">
                <div 
                  class="w-10 h-10 rounded-full flex items-center justify-center"
                  :class="getAffiliateIconName(company) === 'chip' ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-emerald-50 dark:bg-emerald-950/30'"
                >
                  <svg v-if="getAffiliateIconName(company) === 'lightning'" class="w-5 h-5 text-emerald-600 dark:text-emerald-450" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <svg v-else-if="getAffiliateIconName(company) === 'chip'" class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 5h10a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2zM9 9h6v6H9V9z" />
                  </svg>
                  <svg v-else-if="getAffiliateIconName(company) === 'home'" class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <svg v-else class="w-5 h-5 text-emerald-600 dark:text-emerald-450" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
              </div>

              <div>
                <h3 class="text-lg font-black text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors leading-tight mb-2">{{ company.name }}</h3>
                <p v-if="company.description" class="text-gray-700 dark:text-gray-300 text-xs md:text-sm font-normal mt-2 line-clamp-2 leading-relaxed">{{ company.description }}</p>
              </div>
              <div class="mt-8 pt-4 border-t border-gray-100 dark:border-white/5 flex items-center justify-between text-xs md:text-sm font-bold text-emerald-800 dark:text-emerald-300">
                <span class="group-hover:text-emerald-900 dark:group-hover:text-emerald-200 transition-colors">เยี่ยมชมเว็บไซต์</span>
                <div class="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800 group-hover:bg-[#026943] text-gray-700 dark:text-gray-300 group-hover:text-white flex items-center justify-center shadow-sm border border-gray-100 dark:border-gray-750 transition-all duration-300">
                  <svg class="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path></svg>
                </div>
              </div>
            </div>
          </a>
        </div>

        <!-- Footer Outline Button -->
        <div class="flex justify-center mt-12 md:mt-16">

          <router-link to="/contact" class="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#026943] text-[#026943] dark:text-emerald-400 dark:border-emerald-500/50 bg-white dark:bg-slate-900 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 active:scale-95 transition-all text-sm font-bold shadow-sm">
            <svg class="w-4 h-4 text-[#026943] dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            ดูบริษัทในเครือทั้งหมด
            <svg class="w-4 h-4 text-[#026943] dark:text-emerald-400 ml-1 transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </router-link>
        </div>
      </div>
    </section>




    </template>


  </div>
</template>

<style scoped>
.slide-fade-enter-active {
  transition: opacity 1.5s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}
.slide-fade-leave-active {
  transition: opacity 1s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
}
.slide-fade-enter-from {
  opacity: 0;
}
.slide-fade-leave-to {
  opacity: 0;
}

/* Hero staggered text entrance */
.hero-stagger-enter-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
.hero-stagger-leave-active {
  transition: all 0.3s ease;
}
.hero-stagger-enter-from {
  opacity: 0;
  transform: translateY(30px);
}
.hero-stagger-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Stagger delays for child elements */
.hero-stagger-enter-active .hero-stagger-1 { animation: stagger-in 0.6s 0.1s both cubic-bezier(0.16, 1, 0.3, 1); }
.hero-stagger-enter-active .hero-stagger-2 { animation: stagger-in 0.7s 0.2s both cubic-bezier(0.16, 1, 0.3, 1); }
.hero-stagger-enter-active .hero-stagger-3 { animation: stagger-in 0.7s 0.35s both cubic-bezier(0.16, 1, 0.3, 1); }
.hero-stagger-enter-active .hero-stagger-4 { animation: stagger-in 0.7s 0.5s both cubic-bezier(0.16, 1, 0.3, 1); }

@keyframes stagger-in {
  from { opacity: 0; transform: translateY(25px); filter: blur(4px); }
  to { opacity: 1; transform: translateY(0); filter: blur(0); }
}

/* Ken Burns parallax for hero images */
.hero-parallax-img {
  animation: ken-burns 15s ease-in-out infinite alternate;
  will-change: transform;
  backface-visibility: hidden;
}
@keyframes ken-burns {
  0% { transform: scale(1.02) translate(0, 0); }
  100% { transform: scale(1.08) translate(-0.5%, -0.5%); }
}

/* Animated gradient text */
@keyframes gradient-text {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}
.animate-gradient-text {
  animation: gradient-text 4s linear infinite;
}

/* Gradient mesh drift */
@keyframes gradient-drift {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(30px, -30px) rotate(5deg); }
  66% { transform: translate(-20px, 20px) rotate(-3deg); }
}
@keyframes gradient-drift-reverse {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(-30px, 30px) rotate(-5deg); }
  66% { transform: translate(20px, -20px) rotate(3deg); }
}
.animate-gradient-drift {
  animation: gradient-drift 15s ease-in-out infinite;
}
.animate-gradient-drift-reverse {
  animation: gradient-drift-reverse 18s ease-in-out infinite;
}

/* Scroll indicator */
@keyframes scroll-indicator {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(300%); }
}
.animate-scroll-indicator {
  animation: scroll-indicator 2s ease-in-out infinite;
}

.up-fade-enter-active,
.up-fade-leave-active {
  transition: all 0.5s ease;
}
.up-fade-enter-from,
.up-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.text-shadow-lg {
  text-shadow: 0 4px 20px rgba(0,0,0,0.6);
}
.text-shadow-sm {
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

/* Partner logo marquee */
@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.animate-marquee {
  animation: marquee 25s linear infinite;
}
.animate-marquee:hover {
  animation-play-state: paused;
}

/* FAQ answer reveal animation */
.faq-answer-reveal {
  animation: faq-slide-in 0.3s ease-out;
}
@keyframes faq-slide-in {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Floating particles */
@keyframes float-slow {
  0%, 100% { transform: translateY(0) translateX(0); }
  25% { transform: translateY(-30px) translateX(15px); }
  50% { transform: translateY(-15px) translateX(-10px); }
  75% { transform: translateY(-40px) translateX(5px); }
}
@keyframes float-medium {
  0%, 100% { transform: translateY(0) translateX(0); }
  33% { transform: translateY(-20px) translateX(-20px); }
  66% { transform: translateY(-35px) translateX(10px); }
}
@keyframes float-fast {
  0%, 100% { transform: translateY(0) translateX(0); }
  50% { transform: translateY(-25px) translateX(15px); }
}
.animate-float-slow {
  animation: float-slow 8s ease-in-out infinite;
}
.animate-float-medium {
  animation: float-medium 6s ease-in-out infinite;
}
.animate-float-fast {
  animation: float-fast 4s ease-in-out infinite;
}

/* Animated tech grid */
.hero-grid {
  background-image: 
    linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
    linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px);
  background-size: 80px 80px;
  animation: grid-move 20s linear infinite;
}
@keyframes grid-move {
  0% { background-position: 0 0; }
  100% { background-position: 80px 80px; }
}

/* Radial gradient vignette for grid fade */
.hero-grid::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, transparent 30%, #0a0f16 75%);
}


.fade-enter-active, .fade-leave-active {
  transition: opacity 1s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.fade-slide-up-enter-active, .fade-slide-up-leave-active {
  transition: all 0.6s ease;
}
.fade-slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-slide-up-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Cinematic Bento Showcase Custom Keyframes & Animations */
@keyframes float {
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(1deg); }
  100% { transform: translateY(0px) rotate(0deg); }
}
.animate-float {
  animation: float 6s ease-in-out infinite;
}

@keyframes podium-glow {
  0%, 100% {
    transform: scale(1) translateY(0);
    box-shadow: 0 0 30px rgba(16, 185, 129, 0.4), inset 0 0 15px rgba(16, 185, 129, 0.2);
    opacity: 0.4;
  }
  50% {
    transform: scale(1.02) translateY(1px);
    box-shadow: 0 0 55px rgba(16, 185, 129, 0.7), inset 0 0 25px rgba(16, 185, 129, 0.4);
    opacity: 0.6;
  }
}
.animate-podium-glow {
  animation: podium-glow 4s ease-in-out infinite;
}

/* Contact Dropdown Animations */
.dropdown-fade-enter-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
.dropdown-fade-leave-active {
  transition: all 0.15s ease-in;
}
.dropdown-fade-enter-from, .dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}
</style>

